// src/components/helpcenter/HelpCenter.jsx
// Global Help Center modal -- draggable, resizable, z-index managed.
// Two-panel layout: left sidebar nav + right content area.

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Rnd } from 'react-rnd';
import { useModalStore } from '../../state/modalStore';
import { useProfileStore } from '../../state/profileStore';
import { useMessagingStore } from '../../state/messagingStore';
import { useMatrixStore } from '../../state/matrixStore';
import useTeamStore from '../../state/teamStore';
import WALKTHROUGHS from './walkthroughs';
import { searchKnowledgeBase } from './knowledgeBase';
import PasswordStrengthMeter, { MIN_LENGTH as PW_MIN_LENGTH } from '../auth/PasswordStrengthMeter';
import {
  FaRobot,
  FaUser,
  FaCreditCard,
  FaUsers,
  FaShieldAlt,
  FaFileAlt,
  FaTicketAlt,
  FaDownload,
  FaTimes,
  FaWindowMaximize,
  FaWindowRestore,
  FaArrowLeft,
  FaArrowRight,
  FaCheck,
  FaSearch,
  FaIdCard,
  FaComments,
  FaUserPlus,
  FaListUl,
  FaEye,
  FaCheckCircle,
  FaHammer,
  FaHandshake,
  FaEnvelope,
  FaStar,
  FaChartBar,
  FaUpload,
  FaUserEdit,
  FaPaperPlane,
  FaLink,
  FaSignOutAlt,
  FaUserMinus,
  FaCrown,
  FaExchangeAlt,
  FaLock,
  FaShieldCheck,
  FaAt,
  FaEyeSlash,
  FaEye as FaEyeIcon,
  FaGoogle,
  FaQuestionCircle,
} from 'react-icons/fa';

// ── Section keys ──
const SECTIONS = [
  { key: 'ai',            label: 'AI Help',            icon: FaRobot,          ownerOnly: false, captainOnly: false },
  { key: 'account',       label: 'Account',            icon: FaUser,           ownerOnly: false, captainOnly: false },
  { key: 'subscription',  label: 'Subscription',       icon: FaCreditCard,     ownerOnly: true,  captainOnly: false },
  { key: 'team',          label: 'Team Management',    icon: FaUsers,          ownerOnly: false, captainOnly: false },
  { key: 'privacy',       label: 'Privacy',            icon: FaShieldAlt,      ownerOnly: false, captainOnly: false },
  { key: 'faq',            label: 'FAQ',                icon: FaQuestionCircle, ownerOnly: false, captainOnly: false },
  { key: 'support',       label: 'Support Ticket',     icon: FaTicketAlt,      ownerOnly: false, captainOnly: false },
  { key: 'legal',         label: 'Legal',              icon: FaFileAlt,        ownerOnly: false, captainOnly: false },
];

// ── Focus badge pill (matches profile page styling) ──
function FocusBadge({ code, label }) {
  return (
    <div className="flex items-center gap-2">
      <span className="inline-flex items-center text-sm font-bold bg-[#00aef7]/10 px-2.5 py-0.5 rounded-full border border-[#00aef7]/20 text-[#00aef7] whitespace-nowrap">
        {code}
      </span>
      <span className="text-xs text-gray-500">{label}</span>
    </div>
  );
}

// ── Customer Focus content panel ──
function CustomerFocusPanel({ onBack }) {
  return (
    <div className="flex flex-col gap-3">
      {/* Breadcrumb nav */}
      <div className="flex items-center gap-2">
        <button
          onClick={onBack}
          className="text-xs text-gray-400 hover:text-gray-600 transition-colors"
          title="Back to topics"
        >
          ← All topics
        </button>
        <span className="text-xs text-gray-300">|</span>
        <span className="text-xs font-medium text-[#00aef7]">What is customer focus?</span>
      </div>

      <div className="bg-gradient-to-br from-[#00aef7]/5 to-[#00aef7]/10 rounded-xl border border-[#00aef7]/20 p-5 flex flex-col gap-3.5">
        <h4 className="text-base font-semibold text-gray-800">Understanding Customer Focus</h4>

        <p className="text-sm text-gray-600 leading-relaxed">
          Every business on eHandshake has a <strong>customer focus</strong> — it describes who your customers are. This is one of the first things you set up when you create your profile, and it drives how you connect with other professionals on the platform.
        </p>

        <p className="text-sm text-gray-600 leading-relaxed">
          There are three types:
        </p>

        <div className="flex flex-col gap-1.5 py-1">
          <FocusBadge code="B2B" label="A Business that provides products and/or services to other Businesses" />
          <FocusBadge code="B2C" label="A Business that provides products and/or services to Consumers" />
          <FocusBadge code="B2BC" label="A Business that provides products and/or services to Businesses & Consumers both" />
        </div>

        <p className="text-sm text-gray-600 leading-relaxed">
          Example <strong>B2B</strong> businesses:  An accountant, a commercial cleaning company, a business contracts lawyer... Only businesses.
        </p>
        
        <p className="text-sm text-gray-600 leading-relaxed">
          Example <strong>B2C</strong> businesses: A residential plumber, a residential roofer, a hair salon... consumers, not businesses.
        </p>

        <p className="text-sm text-gray-600 leading-relaxed">
            Example <strong>B2BC</strong> businesses: A real estate agent, a catering company, a florist... both consumers and businesses.
        </p>
        <h4 className="text-sm font-semibold text-gray-800 mt-1">Why does it matter?</h4>

        <p className="text-sm text-gray-600 leading-relaxed">
          Customer focus determines who you should be teaming up with. When you join a <strong>team</strong> on eHandshake, you and your teammates share each other's content, profiles, and posts to your social channels. That only works if you're reaching the right audience.
        </p>

        <p className="text-sm text-gray-600 leading-relaxed">
          A B2B accountant sharing a B2C plumber's post to their corporate LinkedIn audience won't generate the best results. But a B2C plumber teaming up with a B2C electrician, painter, and roofer and posting to Facebook makes their audiences overlap perfectly — local homeowners who need services.
        </p>

        <h4 className="text-sm font-semibold text-gray-800 mt-1">Team matching rules</h4>

        <p className="text-sm text-gray-600 leading-relaxed">
          <strong>B2B</strong> users should team with other <strong>B2B</strong> users. <strong>B2C</strong> users should team with other <strong>B2C</strong> users. Their audiences are fundamentally different, and cross-posting between them dilutes the message for everyone.
        </p>

        <p className="text-sm text-gray-600 leading-relaxed">
          <strong>B2BC</strong> users have more flexibility. Since they serve both businesses and consumers, a B2BC user can join a B2B team or a B2C team and add value either way. They're the bridge that can work in both worlds. It is up to the captain of a team to decide if they want that flexibility or prefer teammates with the same focus. 
        </p>

        <p className="text-sm text-gray-600 leading-relaxed">
          When you browse teams to join, eHandshake shows you each team captain's customer focus badge so you can find the right fit. The platform will also flag mismatches during team analysis to help captains build the strongest possible teams.
        </p>


      </div>

      <div className="flex items-center justify-start">
        <button
          onClick={onBack}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors"
        >
          <FaArrowLeft size={10} />
          Back to topics
        </button>
      </div>
    </div>
  );
}

// ── Team info content panel ──
function TeamPanel({ onBack }) {
  return (
    <div className="flex flex-col gap-3">
      {/* Breadcrumb nav */}
      <div className="flex items-center gap-2">
        <button
          onClick={onBack}
          className="text-xs text-gray-400 hover:text-gray-600 transition-colors"
          title="Back to topics"
        >
          ← All topics
        </button>
        <span className="text-xs text-gray-300">|</span>
        <span className="text-xs font-medium text-[#00aef7]">What does a team do for me?</span>
      </div>

      <div className="bg-gradient-to-br from-[#00aef7]/5 to-[#00aef7]/10 rounded-xl border border-[#00aef7]/20 p-5 flex flex-col gap-3.5">
        <h4 className="text-base font-semibold text-gray-800">You're already doing the hard part.</h4>

        <p className="text-sm text-gray-600 leading-relaxed">
          If you're posting to your Facebook page, updating your LinkedIn, or sharing on Instagram — you're already doing <strong>drip marketing</strong>. Consistently showing up so your audience sees you. That works. But there's a ceiling: your audience only grows as fast as your followers do.
        </p>

        <h4 className="text-sm font-semibold text-gray-800 mt-1">The problem with one channel</h4>

        <p className="text-sm text-gray-600 leading-relaxed">
          When you post to your Facebook page, the same 200 people see it. Your regulars. Your existing followers. That's valuable — but it's the same pool every time. You're not reaching the electrician's 400 followers, or the real estate agent's 600 LinkedIn connections, or the attorney's X audience. Those people will never see your name unless someone puts it in front of them.
        </p>

        <h4 className="text-sm font-semibold text-gray-800 mt-1">That's what a team does.</h4>

        <p className="text-sm text-gray-600 leading-relaxed">
          When you join a team on eHandshake, your teammates' creations show up in your Sharing Center — and yours show up in theirs. When you see a teammate's post that you like, you share it to one of your social channels. Your audience sees it. This is when the magic begins.
        </p>

        <p className="text-sm text-gray-600 leading-relaxed">
          Every teammate gets a daily digest showing what was shared and where. They can click through and <strong>reshare</strong> it on their own channels. One creation enters the network and ripples outward — across Facebook pages, LinkedIn profiles, Instagram accounts, and X feeds that the original creator has zero access to.
        </p>

        <h4 className="text-sm font-semibold text-gray-800 mt-1">Think of it this way</h4>

        <p className="text-sm text-gray-600 leading-relaxed">
          You post a promotion to your Facebook page. 200 people see it. That's drip marketing.
        </p>

        <p className="text-sm text-gray-600 leading-relaxed">
          Your teammate shares that same promotion to their LinkedIn followers. 600 new people see it. Another teammate reshares it to their Instagram feed. 350 more. A third teammate puts it on their Facebook page — a completely different audience of 275.
        </p>

        <p className="text-sm text-gray-600 leading-relaxed">
          From one creation, you just reached <strong>1,425 people</strong> across 4 channels instead of 200 on one. Same effort on your part. Zero extra cost.
        </p>

        <h4 className="text-sm font-semibold text-[#00aef7] mt-1">We call it Drip Networking. Drip Marketings big brother.</h4>

        <p className="text-sm text-gray-600 leading-relaxed">
          You already know drip marketing — consistently showing up on your own channels. Drip Networking is the next level. Your content reaches audiences you've never had access to, on platforms you may not even be on, through people who believe in you enough to share your name. You never know who will be scrolling through your teammate's feed and seeing YOUR business for the first time.
        </p>        

        <h4 className="text-sm font-semibold text-gray-800 mt-1">And it's not just creations</h4>

        <p className="text-sm text-gray-600 leading-relaxed">
          Teammates can also share each other's profiles — your user profile, your company profile, and your team profile can all be shared to social channels. A fresh profile share signals to social platforms that you're active and relevant.
        </p>

        <p className="text-sm text-gray-600 leading-relaxed">
          Updated your profile? It becomes available for resharing immediately. Haven't touched it in a while? After 30 days it lights back up in the Sharing Center so a teammate can put it in front of a new audience.
        </p>


      </div>

      <div className="flex items-center justify-start">
        <button
          onClick={onBack}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors"
        >
          <FaArrowLeft size={10} />
          Back to topics
        </button>
      </div>
    </div>
  );
}

// ── Step item for team formation paths ──
function PathStep({ icon: Icon, number, text }) {
  return (
    <div className="flex items-start gap-3">
      <div className="shrink-0 w-7 h-7 rounded-full bg-[#00aef7]/10 flex items-center justify-center mt-0.5">
        <Icon size={13} className="text-[#00aef7]" />
      </div>
      <div className="flex-1">
        <span className="text-xs font-semibold text-gray-400 mr-1.5">Step {number}</span>
        <span className="text-sm text-gray-600">{text}</span>
      </div>
    </div>
  );
}

// ── Team Formation content panel ──
function TeamFormationPanel({ onBack, snapToSidebar }) {
  const navigate = useNavigate();
  const [activePath, setActivePath] = useState(null); // null | 'build' | 'join'

  const handleBuildTeam = () => {
    snapToSidebar?.();
    navigate('/teams');
    // Give the page a moment to mount, then fire the event
    setTimeout(() => window.dispatchEvent(new CustomEvent('team:openRegister')), 600);
  };

  const handleBrowseTeams = () => {
    snapToSidebar?.();
    navigate('/teams');
    setTimeout(() => window.dispatchEvent(new CustomEvent('team:switchLayer', { detail: { layer: 'local' } })), 600);
  };

  return (
    <div className="flex flex-col gap-3">
      {/* Breadcrumb nav */}
      <div className="flex items-center gap-2">
        <button
          onClick={activePath ? () => setActivePath(null) : onBack}
          className="text-xs text-gray-400 hover:text-gray-600 transition-colors"
          title={activePath ? 'Back to paths' : 'Back to topics'}
        >
          ← {activePath ? 'Choose a path' : 'All topics'}
        </button>
        <span className="text-xs text-gray-300">|</span>
        <span className="text-xs font-medium text-[#00aef7]">How are Teams formed?</span>
      </div>

      <div className="bg-gradient-to-br from-[#00aef7]/5 to-[#00aef7]/10 rounded-xl border border-[#00aef7]/20 p-5 flex flex-col gap-3.5">

        {/* Intro — always visible */}
        {!activePath && (
          <>
            <h4 className="text-base font-semibold text-gray-800">Teams start with a choice</h4>

            <p className="text-sm text-gray-600 leading-relaxed">
              A team multiplies your reach. Instead of posting to the same audience every time, your creations flow through your teammates' social channels — reaching people you'd never reach on your own.
            </p>

            <p className="text-sm text-gray-600 leading-relaxed">
              There are two ways to get started:
            </p>

            {/* Two-path fork cards */}
            <div className="grid grid-cols-2 gap-3 mt-1">
              <button
                onClick={() => setActivePath('build')}
                className="flex flex-col items-center gap-2 p-4 rounded-xl border-2 border-[#00aef7]/30 bg-white hover:border-[#00aef7] hover:shadow-md transition-all group"
              >
                <div className="w-12 h-12 rounded-full bg-[#00aef7]/10 flex items-center justify-center group-hover:bg-[#00aef7]/20 transition-colors">
                <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#00aef7"><path d="M739-83.5q-7-2.5-13-8.5L522-296q-6-6-8.5-13t-2.5-15q0-8 2.5-15t8.5-13l85-85q6-6 13-8.5t15-2.5q8 0 15 2.5t13 8.5l204 204q6 6 8.5 13t2.5 15q0 8-2.5 15t-8.5 13l-85 85q-6 6-13 8.5T754-81q-8 0-15-2.5Zm15-92.5 29-29-147-147-29 29 147 147ZM189.5-83q-7.5-3-13.5-9l-84-84q-6-6-9-13.5T80-205q0-8 3-15t9-13l212-212h85l34-34-165-165h-57L80-765l113-113 121 121v57l165 165 116-116-43-43 56-56H495l-28-28 142-142 28 28v113l56-56 142 142q17 17 26 38.5t9 45.5q0 24-9 46t-26 39l-85-85-56 56-42-42-207 207v84L233-92q-6 6-13 9t-15 3q-8 0-15.5-3Zm15.5-93 170-170v-29h-29L176-205l29 29Zm0 0-29-29 15 14 14 15Zm549 0 29-29-29 29Z"/></svg>
                </div>
                <span className="text-sm font-semibold text-gray-800">Build a Team</span>
                <span className="text-[11px] text-gray-400">I want to lead</span>
              </button>

              <button
                onClick={() => setActivePath('join')}
                className="flex flex-col items-center gap-2 p-4 rounded-xl border-2 border-[#00aef7]/30 bg-white hover:border-[#00aef7] hover:shadow-md transition-all group"
              >
                <div className="w-12 h-12 rounded-full bg-[#00aef7]/10 flex items-center justify-center group-hover:bg-[#00aef7]/20 transition-colors">
                <svg
                  className="w-8 h-8"
                  viewBox="0 -960 960 960"
                  fill="#00aef7"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <g transform="translate(0 180)">
                  <path d="m 438.32296,-640 q -66,0 -113,-47 -47,-47 -47,-113 0,-66 47,-113 47,-47 113,-47 66,0 113,47 47,47 47,113 0,66 -47,113 -47,47 -113,47 z m -320,320 v -112 q 0,-34 17.5,-62.5 17.5,-28.5 46.5,-43.5 62,-31 126,-46.5 64,-15.5 130,-15.5 66,0 130,15.5 64,15.5 126,46.5 29,15 46.5,43.5 17.5,28.5 17.5,62.5 v 112 z m 80,-80 h 480 v -32 q 0,-11 -5.5,-20 -5.5,-9 -14.5,-14 -54,-27 -109,-40.5 -55,-13.5 -111,-13.5 -56,0 -111,13.5 -55,13.5 -109,40.5 -9,5 -14.5,14 -5.5,9 -5.5,20 z m 240,-320 q 33,0 56.5,-23.5 23.5,-23.5 23.5,-56.5 0,-33 -23.5,-56.5 -23.5,-23.5 -56.5,-23.5 -33,0 -56.5,23.5 -23.5,23.5 -23.5,56.5 0,33 23.5,56.5 23.5,23.5 56.5,23.5 z" />
                  <path d="m 148.49481,-748.60774 c -20.41779,0 -37.88275,7.93264 -52.422749,23.86194 -14.53984,15.92932 -21.82466,35.70122 -21.82466,59.24903 0,22.85521 7.28482,42.40514 21.82466,58.68089 14.539999,16.2756 32.004959,24.43007 52.422749,24.43007 21.0365,0 38.69978,-8.15447 52.93027,-24.43007 14.23051,-16.27575 21.31712,-35.82568 21.31712,-58.68089 0,-23.54781 -7.08661,-43.31971 -21.31712,-59.24903 -14.23049,-15.9293 -31.89377,-23.86194 -52.93027,-23.86194 z m 593.97923,0 c -20.41775,0 -37.88269,7.93264 -52.42276,23.86194 -14.53987,15.92932 -21.82464,35.70122 -21.82464,59.24903 0,22.85521 7.28477,42.40514 21.82464,58.68089 14.54007,16.2756 32.00501,24.43007 52.42276,24.43007 21.03653,0 38.6998,-8.15447 52.93033,-24.43007 14.23045,-16.27575 21.31709,-35.82568 21.31709,-58.68089 0,-23.54781 -7.08664,-43.31971 -21.31709,-59.24903 -14.23053,-15.9293 -31.8938,-23.86194 -52.93033,-23.86194 z m -593.97923,207.7774 c -44.54781,0 -80.449569,9.38274 -107.673245,28.08242 C 13.5979,-494.04826 0,-469.80685 0,-440.02584 v 65.41741 h 167.05665 v -67.52766 c 0,-17.31448 2.19456,-33.90478 6.52566,-49.83411 4.33107,-15.92931 10.76986,-31.23168 19.43193,-45.77595 -6.80589,-1.38518 -13.89252,-2.25092 -21.31714,-2.59723 -7.42459,-0.34628 -15.15895,-0.48696 -23.20229,-0.48696 z m 593.97923,0 c -7.42463,0 -14.70942,0.14068 -21.82466,0.48696 -7.11539,0.34631 -14.07619,1.21205 -20.88207,2.59723 8.04331,13.85168 14.10515,28.79153 18.12677,44.72084 4.02179,15.92944 6.0181,32.88213 6.0181,50.88922 v 67.52766 h 167.05665 v -65.41741 c 0,-30.47358 -13.59773,-54.93684 -40.82155,-73.29022 -27.22365,-18.35353 -63.12544,-27.51428 -107.67324,-27.51428 z" />
                </g>
                </svg>
                </div>
                <span className="text-sm font-semibold text-gray-800">Join a Team</span>
                <span className="text-[11px] text-gray-400">I want to join</span>
              </button>
            </div>
          </>
        )}

        {/* Build a Team path */}
        {activePath === 'build' && (
          <>
            <h4 className="text-base font-semibold text-gray-800">Build Your Own Team</h4>

            <p className="text-sm text-gray-600 leading-relaxed">
              As a team captain, you choose who joins. You'll name your team, find professionals near you who match your customer focus, and invite them one at a time.
            </p>

            <div className="flex flex-col gap-3 mt-1">
              <PathStep icon={FaUsers} number={1} text="Name your team — pick something that represents your group" />
              <PathStep icon={FaSearch} number={2} text="Browse potential teammates on the Team page — professionals near you who match your customer focus" />
              <PathStep icon={FaIdCard} number={3} text="Click a candidate to view their full profile" />
              <PathStep icon={FaComments} number={4} text="Invite them to chat — get to know them before committing" />
              <PathStep icon={FaUserPlus} number={5} text="Send a team invitation — once they accept, their creations appear in your Sharing Center and yours in theirs" />
            </div>

            <button
              onClick={handleBuildTeam}
              className="mt-2 self-center px-5 py-2 text-sm font-semibold text-white bg-[#00aef7] rounded-lg hover:bg-[#0095d0] transition-colors shadow-sm"
            >
              Build Your Team →
            </button>
          </>
        )}

        {/* Join a Team path */}
        {activePath === 'join' && (
          <>
            <h4 className="text-base font-semibold text-gray-800">Join an Existing Team</h4>

            <p className="text-sm text-gray-600 leading-relaxed">
              If you'd rather join a team than start one, you can browse teams near you that are looking for members. Find a good fit and reach out to the captain.
            </p>

            <div className="flex flex-col gap-3 mt-1">
              <PathStep icon={FaListUl} number={1} text="Browse local teams on the Team page — see who's active in your area" />
              <PathStep icon={FaEye} number={2} text="View a team's profile to see their focus, members, and what they're about" />
              <PathStep icon={FaComments} number={3} text="Start a chat with the team captain to introduce yourself" />
              <PathStep icon={FaCheckCircle} number={4} text="The captain invites you — accept and you're on the team" />
            </div>

            <button
              onClick={handleBrowseTeams}
              className="mt-2 self-center px-5 py-2 text-sm font-semibold text-white bg-[#00aef7] rounded-lg hover:bg-[#0095d0] transition-colors shadow-sm"
            >
              Browse Teams →
            </button>
          </>
        )}
      </div>

      <div className="flex items-center justify-start">
        <button
          onClick={activePath ? () => setActivePath(null) : onBack}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors"
        >
          <FaArrowLeft size={10} />
          {activePath ? 'Back to paths' : 'Back to topics'}
        </button>
      </div>
    </div>
  );
}

// ── Chat Invite content panel ──
function ChatInvitePanel({ onBack, snapToSidebar }) {
  const matrixOpen = useMatrixStore((s) => s.open);
  const setSelectedRoomId = useMatrixStore((s) => s.setSelectedRoomId);
  const setSkipAutoSelect = useMatrixStore((s) => s.setSkipAutoSelect);
  const setSelectedThread = useMessagingStore((s) => s.setSelectedThread);
  const bringToFront = useModalStore((s) => s.bringToFront);

  const handleOpenChatCenter = () => {
    snapToSidebar?.();
    // Same path as docking station: clear selections, open ChatWorld
    setSelectedThread(null);
    setSelectedRoomId(null);
    setSkipAutoSelect(true);
    matrixOpen();
    setTimeout(() => bringToFront('chat'), 100);
  };

  return (
    <div className="flex flex-col gap-3">
      {/* Breadcrumb nav */}
      <div className="flex items-center gap-2">
        <button
          onClick={onBack}
          className="text-xs text-gray-400 hover:text-gray-600 transition-colors"
          title="Back to topics"
        >
          ← All topics
        </button>
        <span className="text-xs text-gray-300">|</span>
        <span className="text-xs font-medium text-[#00aef7]">How do I invite someone to chat?</span>
      </div>

      <div className="bg-gradient-to-br from-[#00aef7]/5 to-[#00aef7]/10 rounded-xl border border-[#00aef7]/20 p-5 flex flex-col gap-3.5">
        <h4 className="text-base font-semibold text-gray-800">Starting a Conversation</h4>

        <p className="text-sm text-gray-600 leading-relaxed">
          Chatting is how relationships begin on eHandshake. Before you team up with someone, before you share content together — you get to know them. Every great professional relationship starts with a conversation.
        </p>

        <h4 className="text-sm font-semibold text-gray-800 mt-1">Three ways to start a chat</h4>

        {/* Way 1 */}
        <div className="flex items-start gap-3">
          <div className="shrink-0 w-7 h-7 rounded-full bg-[#00aef7]/10 flex items-center justify-center mt-0.5">
            <FaComments size={13} className="text-[#00aef7]" />
          </div>
          <div className="flex-1">
            <div className="text-sm font-medium text-gray-800">From the Chat Center</div>
            <p className="text-xs text-gray-500 leading-relaxed mt-0.5">
              Open your Chat Center and you'll see <strong>Discover People Nearby</strong> in the right panel. Browse professionals within 100 miles of you, sorted by distance. Click any users card to show their profile and learn more about them first. Then start a conversation instantly by clicking the chat button on their profile card.
            </p>
          </div>
        </div>

        {/* Way 2 */}
        <div className="flex items-start gap-3">
          <div className="shrink-0 w-7 h-7 rounded-full bg-[#00aef7]/10 flex items-center justify-center mt-0.5">
            <FaSearch size={13} className="text-[#00aef7]" />
          </div>
          <div className="flex-1">
            <div className="text-sm font-medium text-gray-800">From the Team page</div>
            <p className="text-xs text-gray-500 leading-relaxed mt-0.5">
              On the Team page, click any user pin on the map or any name in the candidate list. Their profile card opens with a <strong>chat button</strong> — click it to open a conversation directly.
            </p>
          </div>
        </div>

        {/* Way 3 */}
        <div className="flex items-start gap-3">
          <div className="shrink-0 w-7 h-7 rounded-full bg-[#00aef7]/10 flex items-center justify-center mt-0.5">
            <FaUserPlus size={13} className="text-[#00aef7]" />
          </div>
          <div className="flex-1">
            <div className="text-sm font-medium text-gray-800">Someone invites you</div>
            <p className="text-xs text-gray-500 leading-relaxed mt-0.5">
              When another professional starts a chat with you, it appears automatically in your Chat Center. You'll see a notification and can respond right away.
            </p>
          </div>
        </div>

        <div className="border-t border-[#00aef7]/10 pt-3 mt-1">
          <p className="text-xs text-gray-500 leading-relaxed">
            <strong>Tip:</strong> Chatting is the first step to building a team. Get to know someone before inviting them — see if your customer focus aligns and your service areas overlap. A quick conversation now saves time later.
          </p>
        </div>

        <button
          onClick={handleOpenChatCenter}
          className="mt-1 self-center px-5 py-2 text-sm font-semibold text-white bg-[#00aef7] rounded-lg hover:bg-[#0095d0] transition-colors shadow-sm"
        >
          Open Chat Center →
        </button>
      </div>

      <div className="flex items-center justify-start">
        <button
          onClick={onBack}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors"
        >
          <FaArrowLeft size={10} />
          Back to topics
        </button>
      </div>
    </div>
  );
}

// ── Review Request content panel ──
function ReviewRequestPanel({ onBack, snapToSidebar }) {
  const navigate = useNavigate();

  const handleAddFirstCustomer = () => {
    snapToSidebar?.();
    navigate('/customers');
    // Give the page time to mount, then trigger adding a blank customer card
    setTimeout(() => window.dispatchEvent(new CustomEvent('customers:addNew')), 600);
  };

  const handleGoToCustomers = () => {
    snapToSidebar?.();
    navigate('/customers');
  };

  return (
    <div className="flex flex-col gap-3">
      {/* Breadcrumb nav */}
      <div className="flex items-center gap-2">
        <button
          onClick={onBack}
          className="text-xs text-gray-400 hover:text-gray-600 transition-colors"
          title="Back to topics"
        >
          ← All topics
        </button>
        <span className="text-xs text-gray-300">|</span>
        <span className="text-xs font-medium text-[#00aef7]">How do I send Review Requests?</span>
      </div>

      <div className="bg-gradient-to-br from-[#00aef7]/5 to-[#00aef7]/10 rounded-xl border border-[#00aef7]/20 p-5 flex flex-col gap-3.5">
        <h4 className="text-base font-semibold text-gray-800">Reviews build trust. Trust builds business.</h4>

        <p className="text-sm text-gray-600 leading-relaxed">
          When a potential customer sees your profile on social media, the first thing they look for is <strong>what other people say about you</strong>. Reviews are the most powerful form of marketing because they come from real customers — not from you.
        </p>

        <p className="text-sm text-gray-600 leading-relaxed">
          eHandshake makes it easy to collect reviews from your customers. You add them to your customer list, send a review request, and they receive a secure email with a link to leave their honest feedback. No sign-ups. No friction. And absolutely no anonymous reviews from your competitors. 
        </p>

        <h4 className="text-sm font-semibold text-gray-800 mt-1">How it works</h4>

        {/* Step 1 */}
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-full bg-[#00aef7]/10 flex items-center justify-center shrink-0 mt-0.5">
            <FaUserEdit size={14} className="text-[#00aef7]" />
          </div>
          <div>
            <p className="text-sm font-medium text-gray-800">1. Add your customer</p>
            <p className="text-xs text-gray-500 leading-relaxed">Enter their name, email, and zip code. You can add them one at a time or upload a CSV file to import your entire customer list at once.</p>
          </div>
        </div>

        {/* Step 2 */}
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-full bg-[#00aef7]/10 flex items-center justify-center shrink-0 mt-0.5">
            <FaEnvelope size={14} className="text-[#00aef7]" />
          </div>
          <div>
            <p className="text-sm font-medium text-gray-800">2. Send a review request</p>
            <p className="text-xs text-gray-500 leading-relaxed">Click the envelope icon on their customer card. A pre-written email is ready to send — customize the message if you'd like, then hit send.</p>
          </div>
        </div>

        {/* Step 3 */}
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-full bg-[#00aef7]/10 flex items-center justify-center shrink-0 mt-0.5">
            <FaLink size={14} className="text-[#00aef7]" />
          </div>
          <div>
            <p className="text-sm font-medium text-gray-800">3. They receive a secure link</p>
            <p className="text-xs text-gray-500 leading-relaxed">Your customer gets an email with a personal link to leave their review. No account needed — just click and write. The link is valid for 90 days.</p>
          </div>
        </div>

        {/* Step 4 */}
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-full bg-[#00aef7]/10 flex items-center justify-center shrink-0 mt-0.5">
            <FaStar size={14} className="text-[#00aef7]" />
          </div>
          <div>
            <p className="text-sm font-medium text-gray-800">4. Their review appears on your profile</p>
            <p className="text-xs text-gray-500 leading-relaxed">Once submitted, the review shows up on your eHandshake profile — visible to anyone who views it, including potential customers who find you through a teammate's social post.</p>
          </div>
        </div>

        <h4 className="text-sm font-semibold text-gray-800 mt-1">Track your progress</h4>

        <p className="text-sm text-gray-600 leading-relaxed">
          Your Customers page has a built-in dashboard that shows you exactly where things stand:
        </p>

        <div className="grid grid-cols-3 gap-2">
          <div className="bg-white rounded-lg border border-gray-200 p-2.5 text-center">
            <FaPaperPlane size={14} className="text-[#00aef7] mx-auto mb-1" />
            <p className="text-[10px] font-medium text-gray-600">Requests Sent</p>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-2.5 text-center">
            <FaCheckCircle size={14} className="text-emerald-500 mx-auto mb-1" />
            <p className="text-[10px] font-medium text-gray-600">Completed</p>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-2.5 text-center">
            <FaChartBar size={14} className="text-amber-500 mx-auto mb-1" />
            <p className="text-[10px] font-medium text-gray-600">Replied To</p>
          </div>
        </div>

        <h4 className="text-sm font-semibold text-gray-800 mt-1">Pro tip: Bulk import</h4>

        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-full bg-[#00aef7]/10 flex items-center justify-center shrink-0 mt-0.5">
            <FaUpload size={14} className="text-[#00aef7]" />
          </div>
          <p className="text-xs text-gray-500 leading-relaxed">
            Already have a customer list? Export it from your CRM or spreadsheet as a CSV file and upload it directly. eHandshake will detect duplicates and only add new customers.
          </p>
        </div>

        <p className="text-sm text-gray-600 leading-relaxed mt-1">
          <strong>The more reviews you collect, the stronger your profile becomes</strong> — and when teammates share your profile to their social channels, those reviews travel with it.
        </p>
      </div>

      {/* Action buttons */}
      <div className="flex gap-2 mt-1">
        <button
          onClick={handleAddFirstCustomer}
          className="flex-1 flex items-center justify-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-[#00aef7] hover:bg-[#0095d6] rounded-lg transition-colors"
        >
          <FaUserEdit size={12} />
          Add Your First Customer
        </button>
        <button
          onClick={handleGoToCustomers}
          className="flex-1 flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-[#00aef7] border border-[#00aef7] hover:bg-[#00aef7]/10 rounded-lg transition-colors"
        >
          Go to Customers →
        </button>
      </div>

      <div className="flex items-center justify-start">
        <button
          onClick={onBack}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors"
        >
          <FaArrowLeft size={10} />
          Back to topics
        </button>
      </div>
    </div>
  );
}

// ── AI Chat Panel (Knowledge Base + AI fallback) ──
function AiChatPanel({ onBack, snapToSidebar }) {
  const profile = useProfileStore((s) => s.profile);
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [sessionLoading, setSessionLoading] = useState(true);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const [feedbackGiven, setFeedbackGiven] = useState({}); // { [msgIndex]: 'up' | 'down' | 'forwarded' }

  const API_URL = process.env.REACT_APP_API_URL || '';

  // Find the user question that preceded a given assistant message index
  const findPrecedingQuestion = (assistantIndex) => {
    for (let j = assistantIndex - 1; j >= 0; j--) {
      if (messages[j].role === 'user') return messages[j].message;
    }
    return '';
  };

  const handleFeedback = async (msgIndex, helpful) => {
    const msg = messages[msgIndex];
    const question = findPrecedingQuestion(msgIndex);

    setFeedbackGiven(prev => ({ ...prev, [msgIndex]: helpful ? 'up' : 'down' }));

    try {
      await fetch(`${API_URL}/api/help-kb/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          question,
          aiResponse: msg.message,
          helpful,
        }),
      });

      if (!helpful) {
        // Show "forwarded to the team" message after a brief pause
        setTimeout(() => {
          setFeedbackGiven(prev => ({ ...prev, [msgIndex]: 'forwarded' }));
          setMessages(prev => [...prev, {
            role: 'assistant',
            message: "I want to make sure you get the right answer. I've forwarded your question to the eHandshake team — they'll follow up with you soon.",
            source: 'system',
            created_at: new Date().toISOString(),
          }]);
        }, 500);
      }
    } catch (err) {
      console.error('[AiChat] feedback error:', err);
    }
  };

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Load existing session on mount
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/help-kb/session`, { credentials: 'include' });
        const data = await res.json();
        if (data.ok) {
          setSessionId(data.sessionId);
          setMessages(data.messages || []);
        }
      } catch (err) {
        console.error('[AiChat] Failed to load session:', err);
      }
      setSessionLoading(false);
      // Focus input after loading
      setTimeout(() => inputRef.current?.focus(), 100);
    })();
  }, [API_URL]);

  // Format markdown-like bold text in answers
  const formatMessage = (text) => {
    if (!text) return null;
    return text.split('\n').map((line, i) => {
      // Bold: **text**
      const parts = line.split(/(\*\*[^*]+\*\*)/g);
      const formatted = parts.map((part, j) => {
        if (part.startsWith('**') && part.endsWith('**')) {
          return <strong key={j}>{part.slice(2, -2)}</strong>;
        }
        return part;
      });

      // Bullet points — strip the leading bullet from content to avoid doubling
      if (line.startsWith('• ') || line.startsWith('- ')) {
        const bulletContent = line.replace(/^[•\-]\s*/, '');
        const bulletParts = bulletContent.split(/(\*\*[^*]+\*\*)/g).map((part, j) => {
          if (part.startsWith('**') && part.endsWith('**')) return <strong key={j}>{part.slice(2, -2)}</strong>;
          return part;
        });
        return <div key={i} className="pl-3 flex gap-1.5"><span className="text-[#00aef7] shrink-0">•</span><span>{bulletParts}</span></div>;
      }

      // Numbered steps — strip number prefix from formatted content to avoid doubling
      const numMatch = line.match(/^(\d+)\.\s/);
      if (numMatch) {
        const stepContent = line.replace(/^\d+\.\s*/, '');
        const stepParts = stepContent.split(/(\*\*[^*]+\*\*)/g).map((part, j) => {
          if (part.startsWith('**') && part.endsWith('**')) return <strong key={j}>{part.slice(2, -2)}</strong>;
          return part;
        });
        return <div key={i} className="pl-3 flex gap-1.5"><span className="text-[#00aef7] font-semibold shrink-0">{numMatch[1]}.</span><span>{stepParts}</span></div>;
      }

      // Empty lines = spacing
      if (line.trim() === '') return <div key={i} className="h-2" />;

      return <div key={i}>{formatted}</div>;
    });
  };

  const handleSend = async () => {
    const query = input.trim();
    if (!query || loading) return;

    const sid = sessionId || crypto.randomUUID();
    if (!sessionId) setSessionId(sid);

    // Add user message immediately
    const userMsg = { role: 'user', message: query, created_at: new Date().toISOString() };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setLoading(true);

    try {
      // 1. Search local KB first
      const kbMatches = searchKnowledgeBase(query);

      if (kbMatches.length > 0) {
        // Local KB hit — add a brief "thinking" delay so it feels like AI is working
        await new Promise(resolve => setTimeout(resolve, 2500));

        const bestMatch = kbMatches[0];
        const assistantMsg = {
          role: 'assistant',
          message: bestMatch.answer,
          source: 'local_kb',
          kb_entry_id: bestMatch.id,
          action: bestMatch.action || null,
          created_at: new Date().toISOString(),
        };
        setMessages(prev => [...prev, assistantMsg]);

        // Save to server in background
        fetch(`${API_URL}/api/help-kb/message`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            sessionId: sid,
            userMessage: query,
            assistantMessage: bestMatch.answer,
            source: 'local_kb',
            kbEntryId: bestMatch.id,
          }),
        }).catch(err => console.error('[AiChat] save error:', err));

      } else {
        // 2. No local match — call AI
        const res = await fetch(`${API_URL}/api/help-kb/ai`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            sessionId: sid,
            userMessage: query,
            conversationHistory: messages.slice(-10),
          }),
        });
        const data = await res.json();

        if (data.ok) {
          const assistantMsg = {
            role: 'assistant',
            message: data.message,
            source: 'ai',
            created_at: new Date().toISOString(),
          };
          setMessages(prev => [...prev, assistantMsg]);
        } else {
          setMessages(prev => [...prev, {
            role: 'assistant',
            message: data.error || 'Sorry, something went wrong. Please try again.',
            source: 'ai',
            created_at: new Date().toISOString(),
          }]);
        }
      }
    } catch (err) {
      console.error('[AiChat] error:', err);
      setMessages(prev => [...prev, {
        role: 'assistant',
        message: "I wasn't able to find a good answer for that. I've forwarded your question to the eHandshake team — they'll follow up with you soon.",
        source: 'system',
        created_at: new Date().toISOString(),
      }]);
      // Log as unanswered
      fetch(`${API_URL}/api/help-kb/unanswered`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ question: query }),
      }).catch(() => {});
    }

    setLoading(false);
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const handleNewConversation = async () => {
    const newId = crypto.randomUUID();
    setSessionId(newId);
    setMessages([]);
    setTimeout(() => inputRef.current?.focus(), 50);
    // Create new session on server
    fetch(`${API_URL}/api/help-kb/new-session`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    }).catch(() => {});
  };

  return (
    <div className="flex flex-col h-full" style={{ minHeight: 0 }}>
      {/* Breadcrumb nav */}
      <div className="flex items-center justify-between mb-2 shrink-0">
        <div className="flex items-center gap-2">
          <button
            onClick={onBack}
            className="text-xs text-gray-400 hover:text-gray-600 transition-colors"
            title="Back to topics"
          >
            ← All topics
          </button>
          <span className="text-xs text-gray-300">|</span>
          <span className="text-xs font-medium text-[#00aef7]">AI Knowledge Base</span>
        </div>
        {messages.length > 0 && (
          <button
            onClick={handleNewConversation}
            className="text-[10px] text-gray-400 hover:text-[#00aef7] transition-colors"
            title="Start a new conversation"
          >
            + New Chat
          </button>
        )}
      </div>

      {/* Header */}
      <div className="shrink-0 mb-2">
        <h4 className="text-base font-semibold text-gray-800">eHandshake AI-assisted Knowledge Base</h4>
        <p className="text-xs text-gray-500 mt-0.5">Ask me anything about how eHandshake works.</p>
      </div>

      {/* Messages area */}
      <div className="flex-1 overflow-y-auto min-h-0 mb-2 space-y-3 pr-1" style={{ scrollbarWidth: 'thin' }}>
        {sessionLoading ? (
          <div className="flex items-center justify-center h-full">
            <div className="w-6 h-6 border-2 border-gray-200 border-t-[#00aef7] rounded-full animate-spin" />
          </div>
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center px-4">
            <FaRobot size={32} className="text-[#00aef7]/30 mb-3" />
            <p className="text-sm text-gray-400">
              Ask a question about any eHandshake feature — profiles, teams, sharing, reviews, chat, and more.
            </p>
            <div className="flex flex-wrap justify-center gap-1.5 mt-3">
              {['What is Drip Networking?', 'How do teams work?', 'How do I get started?'].map(q => (
                <button
                  key={q}
                  onClick={() => { setInput(q); setTimeout(() => inputRef.current?.focus(), 50); }}
                  className="px-2.5 py-1 text-[10px] font-medium rounded-full border border-[#00aef7]/30 text-[#00aef7] hover:bg-[#00aef7] hover:text-white transition-colors"
                >
                  {q}
                </button>
              ))}
            </div>
          </div>
        ) : (
          messages.map((msg, i) => (
            <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[85%] rounded-xl px-3 py-2 text-sm leading-relaxed ${
                msg.role === 'user'
                  ? 'bg-[#00aef7] text-white rounded-br-sm'
                  : 'bg-gray-100 text-gray-700 rounded-bl-sm'
              }`}>
                {msg.role === 'assistant' ? (
                  <div className="flex flex-col gap-1 relative">
                    {msg.action && (
                      <button
                        onClick={() => {
                          if (msg.action.snapToSidebar) snapToSidebar?.();
                          if (msg.action.route) navigate(msg.action.route);
                          if (msg.action.event) {
                            setTimeout(() => {
                              window.dispatchEvent(new CustomEvent(msg.action.event, msg.action.eventDetail ? { detail: msg.action.eventDetail } : undefined));
                            }, 600);
                          }
                        }}
                        className="absolute top-0 right-0 flex items-center gap-1 px-2.5 py-1 text-[10px] font-semibold text-white bg-[#00aef7] hover:bg-[#0095d6] rounded-md transition-colors shadow-sm"
                      >
                        {msg.action.label || 'Go'} →
                      </button>
                    )}
                    <div className={msg.action ? 'pr-28' : ''}>{formatMessage(msg.message)}</div>
                    {msg.source && msg.source !== 'system' && (
                      <div className="mt-1.5 flex items-center gap-2">
                        <div className="flex items-center gap-1">
                          <span className={`inline-block w-1.5 h-1.5 rounded-full ${msg.source === 'local_kb' ? 'bg-emerald-400' : 'bg-violet-400'}`} />
                          <span className="text-[9px] text-gray-400">{msg.source === 'local_kb' ? 'Knowledge Base' : 'AI Generated'}</span>
                        </div>
                        {(msg.source === 'ai' || msg.source === 'local_kb') && !feedbackGiven[i] && (
                          <div className="flex items-center gap-1 ml-1">
                            <span className="text-[9px] text-gray-400">Helpful?</span>
                            <button
                              onClick={() => handleFeedback(i, true)}
                              className="text-gray-400 hover:text-emerald-500 transition-colors"
                              title="Yes, this was helpful"
                            >
                              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M14 9V5a3 3 0 00-3-3l-4 9v11h11.28a2 2 0 002-1.7l1.38-9a2 2 0 00-2-2.3H14z M4 22h-1a2 2 0 01-2-2v-7a2 2 0 012-2h1" />
                              </svg>
                            </button>
                            <button
                              onClick={() => handleFeedback(i, false)}
                              className="text-gray-400 hover:text-red-500 transition-colors"
                              title="No, this wasn't helpful"
                            >
                              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M10 15v4a3 3 0 003 3l4-9V2H5.72a2 2 0 00-2 1.7l-1.38 9a2 2 0 002 2.3H10z M20 2h1a2 2 0 012 2v7a2 2 0 01-2 2h-1" />
                              </svg>
                            </button>
                          </div>
                        )}
                        {feedbackGiven[i] === 'up' && (
                          <span className="text-[9px] text-emerald-500 font-medium ml-1">Thanks! 👍</span>
                        )}
                        {feedbackGiven[i] === 'down' && (
                          <span className="text-[9px] text-amber-500 font-medium ml-1">Forwarding to team…</span>
                        )}
                        {feedbackGiven[i] === 'forwarded' && (
                          <span className="text-[9px] text-emerald-500 font-medium ml-1">Forwarded ✓</span>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  msg.message
                )}
              </div>
            </div>
          ))
        )}

        {/* Loading indicator */}
        {loading && (
          <div className="flex justify-start">
            <div className="bg-gray-100 rounded-xl rounded-bl-sm px-4 py-2.5 flex items-center gap-1.5">
              <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
              <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
              <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input area */}
      <div className="shrink-0 flex items-end gap-2 border-t border-gray-200 pt-2">
        <textarea
          ref={inputRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
          placeholder="Type your question..."
          rows={1}
          className="flex-1 resize-none rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800 placeholder-gray-400 focus:border-[#00aef7] focus:ring-1 focus:ring-[#00aef7] outline-none"
          style={{ maxHeight: 80 }}
          disabled={loading}
        />
        <button
          onClick={handleSend}
          disabled={!input.trim() || loading}
          className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 transition-colors ${
            input.trim() && !loading
              ? 'bg-[#00aef7] hover:bg-[#0095d6] text-white'
              : 'bg-gray-200 text-gray-400 cursor-not-allowed'
          }`}
          title="Send"
        >
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
            <line x1="12" y1="19" x2="12" y2="5" />
            <polyline points="5 12 12 5 19 12" />
          </svg>
        </button>
      </div>
    </div>
  );
}

// ── AI Help Panel with walkthrough pills + step card ──
function AIHelpPanel({ snapToSidebar, restoreFromSidebar }) {
  const activeWalkthrough = useModalStore((s) => s.activeWalkthrough);
  const walkthroughStep = useModalStore((s) => s.walkthroughStep);
  const startWalkthrough = useModalStore((s) => s.startWalkthrough);
  const nextStep = useModalStore((s) => s.nextStep);
  const prevStep = useModalStore((s) => s.prevStep);
  const resetWalkthrough = useModalStore((s) => s.resetWalkthrough);
  const completeWalkthrough = useModalStore((s) => s.completeWalkthrough);
  const bringToFront = useModalStore((s) => s.bringToFront);

  // Content panel state for static pages (customer focus, etc.)
  const [contentPanel, setContentPanel] = useState(null);

  const wt = activeWalkthrough ? WALKTHROUGHS[activeWalkthrough] : null;
  const step = wt ? wt.steps[walkthroughStep] : null;
  const isLastStep = wt ? walkthroughStep >= wt.steps.length - 1 : false;
  const isFirstStep = walkthroughStep === 0;

  // When entering a step that has bringToFront, fire it
  useEffect(() => {
    if (step?.bringToFront) {
      bringToFront(step.bringToFront);
    }
  }, [step, bringToFront]);

  // ── Static content panel view ──
  if (contentPanel === 'customer-focus') {
    return <CustomerFocusPanel onBack={() => setContentPanel(null)} />;
  }
  if (contentPanel === 'team-info') {
    return <TeamPanel onBack={() => setContentPanel(null)} />;
  }
  if (contentPanel === 'chat-invite') {
    return <ChatInvitePanel onBack={() => { restoreFromSidebar?.(); setContentPanel(null); }} snapToSidebar={snapToSidebar} />;
  }
  if (contentPanel === 'team-formation') {
    return <TeamFormationPanel onBack={() => { restoreFromSidebar?.(); setContentPanel(null); }} snapToSidebar={snapToSidebar} />;
  }
  if (contentPanel === 'review-requests') {
    return <ReviewRequestPanel onBack={() => { restoreFromSidebar?.(); setContentPanel(null); }} snapToSidebar={snapToSidebar} />;
  }
  if (contentPanel === 'ai-chat') {
    return <AiChatPanel onBack={() => { restoreFromSidebar?.(); setContentPanel(null); }} snapToSidebar={snapToSidebar} />;
  }

  // ── Walkthrough step card view ──
  if (wt && step) {
    return (
      <div className="flex flex-col gap-4">
        <div className="flex items-center gap-2">
          <button
            onClick={() => resetWalkthrough()}
            className="text-xs text-gray-400 hover:text-gray-600 transition-colors"
            title="Back to topics"
          >
            ← All topics
          </button>
          <span className="text-xs text-gray-300">|</span>
          <span className="text-xs font-medium text-[#00aef7]">{wt.label}</span>
        </div>

        {/* Step card */}
        <div className="bg-gradient-to-br from-[#00aef7]/5 to-[#00aef7]/10 rounded-xl border border-[#00aef7]/20 p-5">
          <h4 className="text-base font-semibold text-gray-800 mb-2">{step.title}</h4>
          <div className="flex flex-col gap-2">
            {step.text.split('\n\n').map((para, i) => (
              <p key={i} className="text-sm text-gray-600 leading-relaxed">{para}</p>
            ))}
          </div>

          {/* Pulse hint */}
          {step.pulseTarget && (
            <div className="mt-3 flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg">
              <span className="w-3 h-3 rounded-full bg-amber-400 animate-pulse shrink-0" />
              <span className="text-xs font-medium text-amber-700">
                Look for the glowing gold button on the docking station →
              </span>
            </div>
          )}
        </div>

        {/* Navigation buttons */}
        <div className="flex items-center justify-between">
          <button
            onClick={() => resetWalkthrough()}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors"
          >
            <FaArrowLeft size={10} />
            Back to topics
          </button>

          <button
            onClick={() => completeWalkthrough()}
            className="flex items-center gap-1.5 px-5 py-1.5 text-sm font-semibold text-white bg-[#00aef7] hover:bg-[#0098d8] rounded-lg transition-colors"
          >
            <FaCheck size={10} />
            Done
          </button>
        </div>
      </div>
    );
  }

  // ── Default pill view ──
  // Pills that have a matching walkthrough ID launch the guided walkthrough;
  // 'customer-focus' opens a static content panel; the rest are future AI-chat prompts.
  const PILL_BUTTONS = [
    { label: 'How do I change my profile?',     action: () => startWalkthrough('change-profile') },
    { label: 'What is customer focus?',          action: () => setContentPanel('customer-focus') },
    { label: 'What does a team do for me?',      action: () => setContentPanel('team-info') },
    { label: 'How are Teams formed?',             action: () => setContentPanel('team-formation') },
    { label: 'What is the Design Center?',       action: () => startWalkthrough('create-post') },
    { label: 'What is the Sharing Center?',      action: () => startWalkthrough('share-social') },
    { label: 'How do I invite someone to chat?', action: () => setContentPanel('chat-invite') },
    { label: 'How do I send Review Requests?',   action: () => setContentPanel('review-requests') },
    { label: 'Or Something else?',               action: () => setContentPanel('ai-chat') },
  ];

  return (
    <div className="flex flex-col gap-2">
      <h3 className="text-lg font-semibold text-[#00aef7]">AI Help Assistant</h3>
      <p className="text-sm text-gray-600">
        Ask me anything about how eHandshake works. I can help you understand features,
        walk you through processes, and answer your questions.
      </p>
      <div className="flex flex-wrap gap-1">
        {PILL_BUTTONS.map((pill) => (
          <button
            key={pill.label}
            onClick={() => pill.action ? pill.action() : undefined}
            className={`px-3 py-1 text-xs font-medium rounded-full border transition-colors ${
              pill.action
                ? 'border-[#00aef7] text-[#00aef7] hover:bg-[#00aef7] hover:text-white'
                : 'border-gray-300 text-gray-400 cursor-default'
            }`}
          >
            {pill.label}
          </button>
        ))}
      </div>
      <div className="mt-1 flex-1 min-h-[200px] bg-gradient-to-br from-gray-50 to-[#00aef7]/5 rounded-lg border border-gray-200 p-4 flex flex-col items-center justify-center gap-2">
        <FaRobot size={24} className="text-[#00aef7]/30" />
        <span className="text-sm text-gray-500 font-medium">eHandshake AI-assisted Knowledge Base</span>
        <span className="text-xs text-gray-400 text-center">Click a topic above, or click "Or Something else?" to ask the AI anything about eHandshake.</span>
      </div>
    </div>
  );
}

function AccountPanel({ onOpenTicket }) {
  const profile = useProfileStore((s) => s.profile);
  const API_URL = process.env.REACT_APP_API_URL || '';

  // ── Settings state ──
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [emailAlias, setEmailAlias] = useState('');
  const [aliasSaving, setAliasSaving] = useState(false);
  const [aliasSuccess, setAliasSuccess] = useState('');
  const [aliasError, setAliasError] = useState('');
  const [aliasLocked, setAliasLocked] = useState(false);

  // ── Password state ──
  const [showPwSection, setShowPwSection] = useState(false);
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [showCurrentPw, setShowCurrentPw] = useState(false);
  const [showNewPw, setShowNewPw] = useState(false);
  const [pwSaving, setPwSaving] = useState(false);
  const [pwSuccess, setPwSuccess] = useState('');
  const [pwError, setPwError] = useState('');

  // ── 2FA state ──
  const [totpEnabled, setTotpEnabled] = useState(false);

  // ── 2FA toggle state ──
  const [twoFAStep, setTwoFAStep] = useState(null); // 'enabling' | 'disabling' | null
  const [twoFACode, setTwoFACode] = useState('');
  const [twoFASending, setTwoFASending] = useState(false);
  const [twoFASubmitting, setTwoFASubmitting] = useState(false);
  const [twoFAError, setTwoFAError] = useState('');
  const [twoFASuccess, setTwoFASuccess] = useState('');

  // ── Delete Google Reviews state ──
  const [deleteGoogleConfirm, setDeleteGoogleConfirm] = useState(false);
  const [deleteGoogleInput, setDeleteGoogleInput] = useState('');
  const [deleteGoogleCount, setDeleteGoogleCount] = useState(0);
  const [deleteGoogleLoading, setDeleteGoogleLoading] = useState(false);
  const [deleteGoogleError, setDeleteGoogleError] = useState('');
  const [deleteGoogleSuccess, setDeleteGoogleSuccess] = useState('');

  // Load settings on mount
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/account/settings`, { credentials: 'include' });
        const data = await res.json();
        if (data.ok) {
          setEmail(data.email || '');
          setEmailAlias(data.emailAlias || '');
          if (data.emailAlias) setAliasLocked(true);
          setTotpEnabled(data.totpEnabled || false);
        }
      } catch (err) {
        console.error('[Account] load error:', err);
      }
      setLoading(false);
    })();
  }, [API_URL]);

  // ── Save email alias ──
  const handleSaveAlias = async () => {
    setAliasSaving(true);
    setAliasError('');
    try {
      const res = await fetch(`${API_URL}/api/account/email-alias`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ emailAlias: emailAlias.trim() }),
      });
      const data = await res.json();
      if (data.ok) {
        setAliasSuccess('Email alias saved — verification email sent');
        setAliasLocked(true);
        setTimeout(() => setAliasSuccess(''), 4000);
      } else {
        setAliasError(data.error || 'Failed to save');
      }
    } catch {
      setAliasError('Network error');
    }
    setAliasSaving(false);
  };

  // ── Change password ──
  const handleChangePassword = async () => {
    setPwError('');
    if (!currentPw) return setPwError('Enter your current password');
    if (newPw.length < PW_MIN_LENGTH) return setPwError(`Password must be at least ${PW_MIN_LENGTH} characters`);
    if (newPw !== confirmPw) return setPwError('New passwords do not match');

    setPwSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/account/change-password`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ currentPassword: currentPw, newPassword: newPw }),
      });
      const data = await res.json();
      if (data.ok) {
        setPwSuccess('Password changed successfully');
        setCurrentPw('');
        setNewPw('');
        setConfirmPw('');
        setShowPwSection(false);
        setTimeout(() => setPwSuccess(''), 4000);
      } else {
        setPwError(data.error || 'Failed to change password');
      }
    } catch {
      setPwError('Network error');
    }
    setPwSaving(false);
  };

  // ── Send 2FA verification code to email ──
  const handleSend2FACode = async (action) => {
    setTwoFASending(true);
    setTwoFAError('');
    try {
      const res = await fetch(`${API_URL}/api/account/send-2fa-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      const data = await res.json();
      if (data.ok) {
        setTwoFAStep(action); // 'enabling' or 'disabling'
        setTwoFACode('');
      } else {
        setTwoFAError(data.error || 'Failed to send code');
      }
    } catch {
      setTwoFAError('Network error');
    }
    setTwoFASending(false);
  };

  // ── Toggle 2FA ──
  const handleToggle2FA = async () => {
    setTwoFASubmitting(true);
    setTwoFAError('');
    const enabling = twoFAStep === 'enabling';
    try {
      const res = await fetch(`${API_URL}/api/account/toggle-2fa`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ enabled: enabling, code: twoFACode }),
      });
      const data = await res.json();
      if (data.ok) {
        setTotpEnabled(data.totpEnabled);
        setTwoFAStep(null);
        setTwoFACode('');
        setTwoFASuccess(enabling ? '2FA enabled!' : '2FA disabled');
        setTimeout(() => setTwoFASuccess(''), 3000);
      } else {
        setTwoFAError(data.error || 'Failed to update 2FA');
      }
    } catch {
      setTwoFAError('Network error');
    }
    setTwoFASubmitting(false);
  };

  // ── Delete Google Reviews: start (fetch count) ──
  const handleDeleteGoogleStart = async () => {
    setDeleteGoogleLoading(true);
    setDeleteGoogleError('');
    try {
      const res = await fetch(`${API_URL}/api/account/google-review-count`, { credentials: 'include' });
      const data = await res.json();
      if (data.ok && data.count > 0) {
        setDeleteGoogleCount(data.count);
        setDeleteGoogleConfirm(true);
      } else if (data.count === 0) {
        setDeleteGoogleError('You have no imported Google reviews to delete.');
      } else {
        setDeleteGoogleError(data.error || 'Failed to check reviews');
      }
    } catch {
      setDeleteGoogleError('Network error');
    }
    setDeleteGoogleLoading(false);
  };

  // ── Delete Google Reviews: confirm ──
  const handleDeleteGoogleConfirm = async () => {
    if (deleteGoogleInput !== 'DELETE') return;
    setDeleteGoogleLoading(true);
    setDeleteGoogleError('');
    try {
      const res = await fetch(`${API_URL}/api/account/delete-google-reviews`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      const data = await res.json();
      if (data.ok) {
        setDeleteGoogleConfirm(false);
        setDeleteGoogleInput('');
        setDeleteGoogleSuccess(`${data.deletedCount} Google review${data.deletedCount !== 1 ? 's' : ''} deleted successfully.`);
        setTimeout(() => setDeleteGoogleSuccess(''), 5000);
      } else {
        setDeleteGoogleError(data.error || 'Failed to delete reviews');
      }
    } catch {
      setDeleteGoogleError('Network error');
    }
    setDeleteGoogleLoading(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="w-6 h-6 border-2 border-gray-200 border-t-[#00aef7] rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <h3 className="text-lg font-semibold text-gray-800">Account Settings</h3>

      {/* Success toasts */}
      {(aliasSuccess || pwSuccess || twoFASuccess) && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 flex items-center gap-2">
          <FaCheckCircle size={12} className="text-emerald-500 shrink-0" />
          <span className="text-xs text-emerald-700">{aliasSuccess || pwSuccess || twoFASuccess}</span>
        </div>
      )}

      {/* ── Login Email (read-only) ── */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex items-center gap-2 mb-2">
          <FaEnvelope size={14} className="text-[#00aef7]" />
          <span className="text-sm font-semibold text-gray-800">Login Email</span>
        </div>
        <p className="text-sm text-gray-600 bg-gray-50 rounded px-3 py-1.5 font-mono">{email}</p>
        <p className="text-[10px] text-gray-400 mt-1">This is the email you use to log in. <button type="button" onClick={() => onOpenTicket && onOpenTicket('change_email', 'Request to change my email address')} className="text-[#00aef7] hover:underline font-medium cursor-pointer">Contact support</button> to change it.</p>
      </div>

      {/* ── Email Alias (Reply-To) ── */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex items-center gap-2 mb-2">
          <FaAt size={14} className="text-[#00aef7]" />
          <span className="text-sm font-semibold text-gray-800">Email Alias</span>
        </div>
        <p className="text-[11px] text-gray-500 mb-2">
          When eHandshake sends emails on your behalf (review requests, notifications), replies will go to this address instead of your login email. Leave blank to use your login email.
        </p>
        {aliasLocked ? (
          /* ── Locked view ── */
          <div className="flex items-center gap-2">
            <span className="flex-1 rounded-lg border border-gray-200 bg-gray-50 px-3 py-1.5 text-sm text-gray-700 font-mono">{emailAlias}</span>
            <button
              onClick={() => setAliasLocked(false)}
              className="px-3 py-1.5 text-xs font-medium text-[#00aef7] border border-[#00aef7] rounded-lg hover:bg-[#00aef7]/10 transition-colors"
            >
              Edit
            </button>
            <button
              onClick={async () => {
                setAliasSaving(true);
                try {
                  const res = await fetch(`${API_URL}/api/account/email-alias`, {
                    method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
                    body: JSON.stringify({ emailAlias: '' }),
                  });
                  const data = await res.json();
                  if (data.ok) { setEmailAlias(''); setAliasLocked(false); setAliasSuccess('Email alias removed'); setTimeout(() => setAliasSuccess(''), 3000); }
                } catch { setAliasError('Network error'); }
                setAliasSaving(false);
              }}
              className="px-3 py-1.5 text-xs font-medium text-red-400 border border-red-200 rounded-lg hover:bg-red-50 transition-colors"
              title="Remove alias and use login email"
            >
              Remove
            </button>
          </div>
        ) : (
          /* ── Editable view ── */
          <div className="flex gap-2">
            <input
              type="email"
              value={emailAlias}
              onChange={(e) => { setEmailAlias(e.target.value); setAliasError(''); }}
              placeholder="e.g. mybusiness@aol.com"
              className="flex-1 rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-800 placeholder-gray-400 focus:border-[#00aef7] focus:ring-1 focus:ring-[#00aef7] outline-none"
              autoFocus
            />
            <button
              onClick={handleSaveAlias}
              disabled={aliasSaving || !emailAlias.trim()}
              className="px-4 py-1.5 text-xs font-semibold text-white bg-[#00aef7] hover:bg-[#0095d6] rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {aliasSaving ? 'Saving…' : 'Save'}
            </button>
            {emailAlias && (
              <button
                onClick={() => { setAliasLocked(true); }}
                className="px-3 py-1.5 text-xs text-gray-500 hover:text-gray-700 transition-colors"
              >
                Cancel
              </button>
            )}
          </div>
        )}
        {aliasError && <p className="text-xs text-red-500 mt-1">{aliasError}</p>}
      </div>

      {/* ── Change Password ── */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <FaLock size={14} className="text-[#00aef7]" />
            <span className="text-sm font-semibold text-gray-800">Change Password</span>
          </div>
          {!showPwSection && (
            <button
              onClick={() => setShowPwSection(true)}
              className="px-3 py-1 text-xs font-medium text-[#00aef7] border border-[#00aef7] rounded-lg hover:bg-[#00aef7]/10 transition-colors"
            >
              Change
            </button>
          )}
        </div>

        {showPwSection && (
          <div className="flex flex-col gap-3 mt-2">
            {/* Current password */}
            <div>
              <label className="block text-[11px] font-medium text-gray-600 mb-1">Current Password</label>
              <div className="relative">
                <input
                  type={showCurrentPw ? 'text' : 'password'}
                  value={currentPw}
                  onChange={(e) => { setCurrentPw(e.target.value); setPwError(''); }}
                  className="w-full rounded-lg border border-gray-300 px-3 py-1.5 pr-9 text-sm text-gray-800 focus:border-[#00aef7] focus:ring-1 focus:ring-[#00aef7] outline-none"
                  placeholder="Enter current password"
                />
                <button
                  type="button"
                  onClick={() => setShowCurrentPw(!showCurrentPw)}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showCurrentPw ? <FaEyeSlash size={14} /> : <FaEyeIcon size={14} />}
                </button>
              </div>
            </div>

            {/* New password */}
            <div>
              <label className="block text-[11px] font-medium text-gray-600 mb-1">New Password</label>
              <div className="relative">
                <input
                  type={showNewPw ? 'text' : 'password'}
                  value={newPw}
                  onChange={(e) => { setNewPw(e.target.value); setPwError(''); }}
                  className="w-full rounded-lg border border-gray-300 px-3 py-1.5 pr-9 text-sm text-gray-800 focus:border-[#00aef7] focus:ring-1 focus:ring-[#00aef7] outline-none"
                  placeholder="Enter new password"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPw(!showNewPw)}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showNewPw ? <FaEyeSlash size={14} /> : <FaEyeIcon size={14} />}
                </button>
              </div>
              {newPw && <div className="mt-2"><PasswordStrengthMeter password={newPw} /></div>}
            </div>

            {/* Confirm new password */}
            <div>
              <label className="block text-[11px] font-medium text-gray-600 mb-1">Confirm New Password</label>
              <input
                type="password"
                value={confirmPw}
                onChange={(e) => { setConfirmPw(e.target.value); setPwError(''); }}
                className={`w-full rounded-lg border px-3 py-1.5 text-sm text-gray-800 focus:ring-1 outline-none ${
                  confirmPw && confirmPw !== newPw
                    ? 'border-red-300 focus:border-red-400 focus:ring-red-300'
                    : 'border-gray-300 focus:border-[#00aef7] focus:ring-[#00aef7]'
                }`}
                placeholder="Re-enter new password"
              />
              {confirmPw && confirmPw !== newPw && (
                <p className="text-[10px] text-red-500 mt-0.5">Passwords do not match</p>
              )}
            </div>

            {pwError && <p className="text-xs text-red-500">{pwError}</p>}

            <div className="flex gap-2">
              <button
                onClick={handleChangePassword}
                disabled={pwSaving || !currentPw || !newPw || newPw !== confirmPw || newPw.length < PW_MIN_LENGTH}
                className="px-4 py-1.5 text-xs font-semibold text-white bg-[#00aef7] hover:bg-[#0095d6] rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {pwSaving ? 'Saving…' : 'Update Password'}
              </button>
              <button
                onClick={() => { setShowPwSection(false); setCurrentPw(''); setNewPw(''); setConfirmPw(''); setPwError(''); }}
                className="px-4 py-1.5 text-xs text-gray-500 hover:text-gray-700 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── Two-Factor Authentication ── */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <FaShieldAlt size={14} className="text-[#00aef7]" />
            <span className="text-sm font-semibold text-gray-800">Two-Factor Authentication</span>
          </div>
          {totpEnabled ? (
            <span className="px-2 py-0.5 text-[10px] font-semibold rounded-full bg-emerald-100 text-emerald-700">Enabled</span>
          ) : (
            <span className="px-2 py-0.5 text-[10px] font-semibold rounded-full bg-gray-100 text-gray-500">Disabled</span>
          )}
        </div>
        <p className="text-[11px] text-gray-500 mb-2">
          Add an extra layer of security to your account. When enabled, you'll receive a 6-digit verification code to your login email each time you log in.
        </p>

        {!twoFAStep ? (
          /* ── Default: Enable / Disable button ── */
          <>
            {totpEnabled ? (
              <button
                onClick={() => handleSend2FACode('disabling')}
                disabled={twoFASending}
                className="px-4 py-1.5 text-xs font-medium text-red-500 border border-red-200 rounded-lg hover:bg-red-50 transition-colors disabled:opacity-50"
              >
                {twoFASending ? 'Sending code…' : 'Disable 2FA'}
              </button>
            ) : (
              <button
                onClick={() => handleSend2FACode('enabling')}
                disabled={twoFASending}
                className="px-4 py-1.5 text-xs font-semibold text-white bg-[#00aef7] hover:bg-[#0095d6] rounded-lg transition-colors disabled:opacity-50"
              >
                {twoFASending ? 'Sending code…' : 'Enable 2FA'}
              </button>
            )}
            {twoFAError && !twoFAStep && <p className="text-xs text-red-500 mt-1">{twoFAError}</p>}
          </>
        ) : (
          /* ── Code entry for enabling/disabling ── */
          <div className="flex flex-col gap-2">
            <p className="text-[10px] text-gray-500">
              Enter the 6-digit code sent to your email to {twoFAStep === 'enabling' ? 'enable' : 'disable'} 2FA:
            </p>
            <div className="flex gap-2">
              <input
                type="text"
                inputMode="numeric"
                value={twoFACode}
                onChange={(e) => { setTwoFACode(e.target.value.replace(/\D/g, '').slice(0, 6)); setTwoFAError(''); }}
                placeholder="000000"
                className="w-28 rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-center font-mono tracking-widest text-gray-800 focus:border-[#00aef7] focus:ring-1 focus:ring-[#00aef7] outline-none"
                maxLength={6}
                autoFocus
              />
              <button
                onClick={handleToggle2FA}
                disabled={twoFASubmitting || twoFACode.length !== 6}
                className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                  twoFAStep === 'enabling'
                    ? 'text-white bg-[#00aef7] hover:bg-[#0095d6]'
                    : 'text-red-600 border border-red-300 hover:bg-red-50'
                }`}
              >
                {twoFASubmitting ? 'Verifying…' : twoFAStep === 'enabling' ? 'Confirm Enable' : 'Confirm Disable'}
              </button>
              <button
                onClick={() => { setTwoFAStep(null); setTwoFACode(''); setTwoFAError(''); }}
                className="px-2 py-1.5 text-[10px] text-gray-400 hover:text-gray-600 transition-colors"
              >
                Cancel
              </button>
            </div>
            {twoFAError && <p className="text-[10px] text-red-500">{twoFAError}</p>}
          </div>
        )}
      </div>

      {/* ── Danger Zone ── */}
      <hr className="border-gray-200" />
      <div className="flex flex-col gap-3">
        {/* ── Delete Google Reviews ── */}
        <div className="bg-white rounded-lg border border-red-200 p-4">
          <div className="flex items-center gap-2 mb-1">
            <FaGoogle size={14} className="text-red-400" />
            <span className="text-sm font-semibold text-gray-800">Delete Google Reviews</span>
          </div>
          <p className="text-[11px] text-gray-500 mb-3">
            Permanently remove all imported Google reviews from your account. This cannot be undone. You will be able to re-import after deletion.
          </p>

          {deleteGoogleSuccess && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 mb-3 flex items-center gap-2">
              <FaCheckCircle size={12} className="text-emerald-500 shrink-0" />
              <span className="text-xs text-emerald-700">{deleteGoogleSuccess}</span>
            </div>
          )}

          {!deleteGoogleConfirm ? (
            <div>
              <button
                onClick={handleDeleteGoogleStart}
                disabled={deleteGoogleLoading}
                className="px-4 py-1.5 text-xs font-medium text-red-500 border border-red-200 rounded-lg hover:bg-red-50 transition-colors disabled:opacity-50"
              >
                {deleteGoogleLoading ? 'Checking…' : 'Delete All Google Reviews'}
              </button>
              {deleteGoogleError && <p className="text-xs text-red-500 mt-1">{deleteGoogleError}</p>}
            </div>
          ) : (
            <div className="flex flex-col gap-2 bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-xs text-red-700 font-medium">
                This will permanently delete all {deleteGoogleCount} imported Google review{deleteGoogleCount !== 1 ? 's' : ''}. This cannot be undone.
              </p>
              <p className="text-[10px] text-red-600">
                Type <span className="font-mono font-bold">DELETE</span> to confirm:
              </p>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={deleteGoogleInput}
                  onChange={(e) => { setDeleteGoogleInput(e.target.value.toUpperCase()); setDeleteGoogleError(''); }}
                  placeholder="Type DELETE"
                  className="w-32 rounded-lg border border-red-300 px-3 py-1.5 text-sm font-mono text-gray-800 focus:border-red-400 focus:ring-1 focus:ring-red-300 outline-none"
                  autoFocus
                />
                <button
                  onClick={handleDeleteGoogleConfirm}
                  disabled={deleteGoogleLoading || deleteGoogleInput !== 'DELETE'}
                  className="px-3 py-1.5 text-xs font-semibold text-white bg-red-500 hover:bg-red-600 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {deleteGoogleLoading ? 'Deleting…' : 'Confirm Delete'}
                </button>
                <button
                  onClick={() => { setDeleteGoogleConfirm(false); setDeleteGoogleInput(''); setDeleteGoogleError(''); }}
                  className="px-2 py-1.5 text-[10px] text-gray-400 hover:text-gray-600 transition-colors"
                >
                  Cancel
                </button>
              </div>
              {deleteGoogleError && <p className="text-[10px] text-red-500">{deleteGoogleError}</p>}
            </div>
          )}
        </div>

        {/* ── Delete Account ── */}
        <div className="bg-white rounded-lg border border-red-200 p-4">
          <div className="flex items-center gap-2 mb-1">
            <FaUserMinus size={14} className="text-red-400" />
            <span className="text-sm font-semibold text-gray-800">Delete Account</span>
          </div>
          <p className="text-[11px] text-gray-500 mb-3">
            Permanently delete your account and all associated data. This action is irreversible and requires support verification.
          </p>
          <button
            onClick={() => onOpenTicket && onOpenTicket('delete_account')}
            className="inline-flex items-center gap-1.5 px-4 py-1.5 text-xs font-medium text-red-500 border border-red-200 rounded-lg hover:bg-red-50 transition-colors"
          >
            <FaTicketAlt size={10} />
            Open Deletion Request
          </button>
          <p className="text-[10px] text-gray-400 mt-2">
            For your security, account deletions are handled by our support team and completed within 48 hours.
          </p>
        </div>
      </div>
    </div>
  );
}

function SubscriptionPanel() {
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState('');
  const API_URL = process.env.REACT_APP_API_URL || '';

  const TIER_LABELS = { free_agent: 'Free Agent', first_string: 'First String', team_member: 'Team Member', team_captain_fulfilled: 'Team Captain' };
  const TIER_PRICES = { free_agent: '$0', first_string: '$19.99/mo', team_member: '$49.99/mo' };

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/subscription/status`, { credentials: 'include' });
        const data = await res.json();
        if (data.ok) setSub(data);
      } catch (err) {
        console.error('[Subscription] load error:', err);
      }
      setLoading(false);
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCheckout = async (tier, interval = 'month') => {
    setActionLoading('checkout');
    try {
      const res = await fetch(`${API_URL}/api/subscription/create-checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ tier, interval }),
      });
      const data = await res.json();
      if (data.ok && data.url) {
        if (window.LemonSqueezy?.Url?.Open) window.LemonSqueezy.Url.Open(data.url);
        else window.open(data.url, '_blank');
      } else console.error('[Subscription] checkout error:', data.error);
    } catch (err) {
      console.error('[Subscription] checkout error:', err);
    }
    setActionLoading('');
  };

  const handlePortal = async () => {
    setActionLoading('portal');
    try {
      const res = await fetch(`${API_URL}/api/subscription/portal`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      const data = await res.json();
      if (data.ok && data.url) window.open(data.url, '_blank');
      else console.error('[Subscription] portal error:', data.error);
    } catch (err) {
      console.error('[Subscription] portal error:', err);
    }
    setActionLoading('');
  };

  if (loading) {
    return (
      <div className="flex flex-col gap-4">
        <h3 className="text-lg font-semibold text-gray-800">Subscription & Billing</h3>
        <div className="text-sm text-gray-500">Loading...</div>
      </div>
    );
  }

  const tier = sub?.tier || 'free_agent';
  const tierLabel = TIER_LABELS[tier] || 'Free Agent';
  const tierPrice = TIER_PRICES[tier] || '$0';
  const limit = sub?.limit;
  const activeCount = sub?.activeCreationCount || 0;
  const billingInfo = sub?.subscription;
  const isPaid = billingInfo && billingInfo.status === 'active';

  return (
    <div className="flex flex-col gap-4">
      <h3 className="text-lg font-semibold text-gray-800">Subscription & Billing</h3>

      {/* Current plan card */}
      <div className="bg-gray-50 rounded-lg border border-gray-200 p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-semibold text-gray-800">Current Plan</p>
            <p className="text-2xl font-bold text-[#00aef7]">{tierLabel}</p>
            <p className="text-xs text-gray-500 mt-0.5">{tierPrice}</p>
          </div>
          <span className={`px-3 py-1 text-xs font-semibold rounded-full ${
            billingInfo?.status === 'past_due' ? 'bg-red-100 text-red-700' :
            billingInfo?.cancelAtPeriodEnd ? 'bg-amber-100 text-amber-700' :
            'bg-green-100 text-green-700'
          }`}>
            {billingInfo?.status === 'past_due' ? 'Past Due' :
             billingInfo?.cancelAtPeriodEnd ? 'Canceling' : 'Active'}
          </span>
        </div>

        {/* Renewal info */}
        {billingInfo?.currentPeriodEnd && (
          <p className="text-[11px] text-gray-400 mt-2">
            {billingInfo.cancelAtPeriodEnd
              ? `Plan ends ${new Date(billingInfo.currentPeriodEnd).toLocaleDateString()}`
              : `Renews ${new Date(billingInfo.currentPeriodEnd).toLocaleDateString()}`}
          </p>
        )}
      </div>

      {/* Creation slot usage */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-semibold text-gray-800">Active Creations</span>
          <span className="text-sm font-bold text-gray-600">
            {activeCount} / {limit === null ? '∞' : limit}
          </span>
        </div>
        {limit !== null && (
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className={`h-2 rounded-full transition-all ${
                activeCount >= limit ? 'bg-red-500' :
                activeCount >= limit - 1 ? 'bg-amber-400' : 'bg-[#00aef7]'
              }`}
              style={{ width: `${Math.min((activeCount / limit) * 100, 100)}%` }}
            />
          </div>
        )}
        {limit !== null && activeCount >= limit && (
          <p className="text-[11px] text-red-500 mt-1">
            You've reached your limit. Delete a creation or upgrade to publish more.
          </p>
        )}
      </div>

      {/* Action buttons */}
      <div className="flex flex-col gap-3">
        {/* Upgrade options — show if not on team_member */}
        {tier !== 'team_member' && tier !== 'team_captain_fulfilled' && (
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center gap-2 mb-2">
              <FaCreditCard size={14} className="text-[#00aef7]" />
              <span className="text-sm font-semibold text-gray-800">Upgrade Plan</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {tier === 'free_agent' && (
                <button
                  onClick={() => handleCheckout('first_string', 'month')}
                  disabled={!!actionLoading}
                  className="px-4 py-1.5 text-xs font-semibold text-white bg-[#00aef7] hover:bg-[#0095d6] rounded-lg transition-colors disabled:opacity-50"
                >
                  {actionLoading === 'checkout' ? 'Loading...' : 'First String — $19.99/mo'}
                </button>
              )}
              <button
                onClick={() => handleCheckout('team_member', 'month')}
                disabled={!!actionLoading}
                className="px-4 py-1.5 text-xs font-semibold text-white bg-[#00aef7] hover:bg-[#0095d6] rounded-lg transition-colors disabled:opacity-50"
              >
                {actionLoading === 'checkout' ? 'Loading...' : 'Team Member — $49.99/mo'}
              </button>
            </div>
          </div>
        )}

        {/* Manage Billing — only for paid subscribers */}
        {isPaid && (
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <span className="text-sm font-semibold text-gray-800">Manage Billing</span>
                <p className="text-[11px] text-gray-500">View invoices, update payment method, or cancel</p>
              </div>
              <button
                onClick={handlePortal}
                disabled={!!actionLoading}
                className="px-4 py-1.5 text-xs font-semibold text-[#00aef7] border border-[#00aef7] hover:bg-[#00aef7]/5 rounded-lg transition-colors disabled:opacity-50"
              >
                {actionLoading === 'portal' ? 'Loading...' : 'Open Billing Portal'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function TeamManagementPanel({ isCaptain }) {
  const profile = useProfileStore((s) => s.profile);
  const myTeams = useTeamStore((s) => s.myTeams);
  const setMyTeams = useTeamStore((s) => s.setMyTeams);
  const ensureMyTeamsHydrated = useTeamStore((s) => s.ensureMyTeamsHydrated);
  const [leaving, setLeaving] = useState(null); // team_id being left
  const [confirmLeave, setConfirmLeave] = useState(null); // team_id awaiting confirmation
  const [leaveError, setLeaveError] = useState('');
  const [leaveSuccess, setLeaveSuccess] = useState('');
  const [removing, setRemoving] = useState(null); // { team_id, u_id } being removed
  const [confirmRemove, setConfirmRemove] = useState(null); // { team_id, u_id, name }

  // Fetch teams on mount
  useEffect(() => {
    if (profile?.u_id) ensureMyTeamsHydrated(profile.u_id);
  }, [profile?.u_id, ensureMyTeamsHydrated]);

  const handleLeaveTeam = async (teamId) => {
    setLeaving(teamId);
    setLeaveError('');
    try {
      const res = await fetch('/api/teams/leave', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ team_id: teamId }),
      });
      const data = await res.json();
      if (data.ok) {
        setLeaveSuccess('You have left the team.');
        setConfirmLeave(null);
        // Remove team from local state immediately
        setMyTeams((prev) => prev.filter(t => t.team_id !== teamId));
        setTimeout(() => setLeaveSuccess(''), 4000);
      } else {
        setLeaveError(data.error || 'Failed to leave team.');
      }
    } catch (err) {
      setLeaveError('Network error. Please try again.');
    }
    setLeaving(null);
  };

  const handleRemoveMember = async (teamId, memberId) => {
    setRemoving({ team_id: teamId, u_id: memberId });
    setLeaveError('');
    try {
      const res = await fetch('/api/teams/remove-member', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ team_id: teamId, member_u_id: memberId }),
      });
      const data = await res.json();
      if (data.ok) {
        setLeaveSuccess('Teammate removed.');
        setConfirmRemove(null);
        if (profile?.u_id) ensureMyTeamsHydrated(profile.u_id);
        setTimeout(() => setLeaveSuccess(''), 4000);
      } else {
        setLeaveError(data.error || 'Failed to remove teammate.');
      }
    } catch (err) {
      setLeaveError('Network error. Please try again.');
    }
    setRemoving(null);
  };

  const userId = profile?.u_id;

  return (
    <div className="flex flex-col gap-4">
      <h3 className="text-lg font-semibold text-gray-800">Team Management</h3>

      {leaveSuccess && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 flex items-center gap-2">
          <FaCheckCircle size={14} className="text-emerald-500 shrink-0" />
          <p className="text-sm text-emerald-700">{leaveSuccess}</p>
        </div>
      )}

      {leaveError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2">
          <span className="text-red-500 shrink-0">⚠</span>
          <p className="text-sm text-red-700">{leaveError}</p>
          <button onClick={() => setLeaveError('')} className="ml-auto text-red-400 hover:text-red-600">&times;</button>
        </div>
      )}

      {(!myTeams || myTeams.length === 0) ? (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-center">
          <FaUsers size={24} className="text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-gray-500">You're not on any teams yet.</p>
          <p className="text-xs text-gray-400 mt-1">Join or create a team from the Team Info page to get started.</p>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {myTeams.map((team) => {
            const isCaptainOfThis = team.captain_id === userId || team.role === 'captain';
            const members = team.members || [];
            const otherMembers = members.filter(m => m.u_id !== userId);

            return (
              <div key={team.team_id} className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
                {/* Team header */}
                <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-[#00aef7]/5 to-white border-b border-gray-100">
                  <div className="flex items-center gap-2">
                    <FaUsers size={14} className="text-[#00aef7]" />
                    <span className="text-sm font-semibold text-gray-800">{team.team_name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {isCaptainOfThis && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-medium text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">
                        <FaCrown size={8} /> Captain
                      </span>
                    )}
                    <span className="text-[10px] text-gray-400">{members.length} member{members.length !== 1 ? 's' : ''}</span>
                  </div>
                </div>

                {/* Team actions */}
                <div className="px-4 py-2.5">
                  {/* Leave team (for non-captains) */}
                  {!isCaptainOfThis && (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <FaSignOutAlt size={12} className="text-gray-400" />
                        <span className="text-xs text-gray-600">Leave this team</span>
                      </div>
                      {confirmLeave === team.team_id ? (
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] text-red-500 font-medium">Are you sure?</span>
                          <button
                            onClick={() => handleLeaveTeam(team.team_id)}
                            disabled={leaving === team.team_id}
                            className="px-2.5 py-1 text-[10px] font-semibold text-white bg-red-500 hover:bg-red-600 rounded transition-colors disabled:opacity-50"
                          >
                            {leaving === team.team_id ? 'Leaving…' : 'Yes, Leave'}
                          </button>
                          <button
                            onClick={() => setConfirmLeave(null)}
                            className="px-2.5 py-1 text-[10px] text-gray-500 hover:text-gray-700 transition-colors"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setConfirmLeave(team.team_id)}
                          className="px-3 py-1 text-[10px] font-medium text-red-500 border border-red-200 hover:bg-red-50 rounded transition-colors"
                        >
                          Leave Team
                        </button>
                      )}
                    </div>
                  )}

                  {/* Captain-only: transfer captaincy */}
                  {isCaptainOfThis && (
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <FaExchangeAlt size={12} className="text-gray-400" />
                        <span className="text-xs text-gray-600">Transfer captain role</span>
                      </div>
                      <span className="text-[10px] text-gray-400 italic">Coming soon</span>
                    </div>
                  )}

                  {/* Captain-only: remove members */}
                  {isCaptainOfThis && otherMembers.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-gray-100">
                      <div className="flex items-center gap-2 mb-2">
                        <FaUserMinus size={12} className="text-gray-400" />
                        <span className="text-xs text-gray-600 font-medium">Remove a teammate</span>
                      </div>
                      <div className="flex flex-col gap-1.5">
                        {otherMembers.map((member) => (
                          <div key={member.u_id} className="flex items-center justify-between py-1 px-2 rounded hover:bg-gray-50">
                            <span className="text-xs text-gray-700">
                              {member.first_name || member.u_first_name} {member.last_name || member.u_last_name}
                            </span>
                            {confirmRemove?.team_id === team.team_id && confirmRemove?.u_id === member.u_id ? (
                              <div className="flex items-center gap-1.5">
                                <span className="text-[9px] text-red-500">Sure?</span>
                                <button
                                  onClick={() => handleRemoveMember(team.team_id, member.u_id)}
                                  disabled={removing?.u_id === member.u_id}
                                  className="px-2 py-0.5 text-[9px] font-semibold text-white bg-red-500 hover:bg-red-600 rounded transition-colors disabled:opacity-50"
                                >
                                  {removing?.u_id === member.u_id ? '…' : 'Remove'}
                                </button>
                                <button onClick={() => setConfirmRemove(null)} className="text-[9px] text-gray-400 hover:text-gray-600">Cancel</button>
                              </div>
                            ) : (
                              <button
                                onClick={() => setConfirmRemove({ team_id: team.team_id, u_id: member.u_id, name: `${member.first_name || member.u_first_name}` })}
                                className="text-[9px] text-red-400 hover:text-red-600 transition-colors"
                              >
                                Remove
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Captain: can't leave without transferring */}
                  {isCaptainOfThis && (
                    <div className="mt-2 pt-2 border-t border-gray-100">
                      <p className="text-[10px] text-gray-400 italic">
                        As captain, you must transfer captaincy before you can leave this team.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function PrivacyPanel() {
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState('');
  const [sharingOptOut, setSharingOptOut] = useState(false);
  const [sharingOptOutSaving, setSharingOptOutSaving] = useState(false);
  const [sharingLoaded, setSharingLoaded] = useState(false);
  const API_URL = process.env.REACT_APP_API_URL || '';

  // Load sharing opt-out state
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/account/settings`, { credentials: 'include' });
        const data = await res.json();
        if (data.ok) setSharingOptOut(data.sharingOptOut || false);
      } catch (err) {
        console.error('[Privacy] load sharing opt-out error:', err);
      }
      setSharingLoaded(true);
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleExport = async () => {
    setExporting(true);
    setExportError('');
    try {
      const res = await fetch(`${API_URL}/api/account/export`, { credentials: 'include' });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Export failed');
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ehandshake-data-export-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setExportError(err.message || 'Failed to export data');
    }
    setExporting(false);
  };

  return (
    <div className="flex flex-col gap-4">
      <h3 className="text-lg font-semibold text-gray-800">Privacy Settings</h3>
      <div className="flex flex-col gap-3">
        {/* ── Sharing Opt-Out Toggle ── */}
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <label className="flex items-start gap-3 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={!sharingOptOut}
              disabled={sharingOptOutSaving || !sharingLoaded}
              onChange={async (e) => {
                const newOptOut = !e.target.checked;
                setSharingOptOutSaving(true);
                try {
                  const res = await fetch(`${API_URL}/api/account/sharing-opt-out`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'include',
                    body: JSON.stringify({ optOut: newOptOut }),
                  });
                  const data = await res.json();
                  if (data.ok) setSharingOptOut(data.sharingOptOut);
                } catch (err) {
                  console.error('[Privacy] sharing opt-out error:', err);
                }
                setSharingOptOutSaving(false);
              }}
              className="mt-0.5 h-4 w-4 rounded border-gray-300 focus:ring-[#00aef7] shrink-0"
              style={{ accentColor: '#00aef7' }}
            />
            <div>
              <span className="text-sm font-semibold text-gray-800">Allow Free Agent, First String and Teammates to share my profile</span>
              <p className="text-[11px] text-gray-500 mt-0.5">
                When enabled, other users can share your contact card and company on social platforms and via email. Uncheck to temporarily pause all sharing of your profile.
              </p>
            </div>
          </label>
        </div>

        {/* ── Data Export ── */}
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center gap-2 mb-1">
            <FaDownload size={14} className="text-[#00aef7]" />
            <span className="text-sm font-semibold text-gray-800">Data Export</span>
          </div>
          <p className="text-[11px] text-gray-500 mb-3">
            Download a copy of all your data including your profile, business info, customers, reviews, and team memberships.
          </p>
          <button
            onClick={handleExport}
            disabled={exporting}
            className="inline-flex items-center gap-1.5 px-4 py-1.5 text-xs font-semibold text-white bg-[#00aef7] hover:bg-[#0095d6] rounded-lg transition-colors disabled:opacity-50"
          >
            <FaDownload size={10} />
            {exporting ? 'Preparing download…' : 'Download My Data'}
          </button>
          {exportError && <p className="text-xs text-red-500 mt-1">{exportError}</p>}
        </div>
      </div>
    </div>
  );
}

const TICKET_TYPES = [
  { value: 'bug', label: 'Report a Bug' },
  { value: 'delete_account', label: 'Delete My Account' },
  { value: 'change_email', label: 'Change Login Email' },
  { value: 'billing', label: 'Billing Question' },
  { value: 'feature_request', label: 'Feature Request' },
  { value: 'other', label: 'Other' },
];

function SupportTicketPanel({ preSelectedType, preFilledSubject }) {
  const [ticketType, setTicketType] = useState(preSelectedType || '');
  const [subject, setSubject] = useState(preFilledSubject || '');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitted, setSubmitted] = useState(null); // ticket ID on success
  const [exporting, setExporting] = useState(false);
  const API_URL = process.env.REACT_APP_API_URL || '';

  // Update if preSelectedType changes
  useEffect(() => {
    if (preSelectedType && preSelectedType !== ticketType) {
      setTicketType(preSelectedType);
      setSubmitted(null);
      setSubmitError('');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preSelectedType]);

  useEffect(() => {
    if (preFilledSubject) setSubject(preFilledSubject);
  }, [preFilledSubject]);

  const handleSubmit = async () => {
    setSubmitError('');
    if (!ticketType) return setSubmitError('Please select a ticket type');
    if (!subject.trim() || subject.trim().length < 3) return setSubmitError('Subject is required (at least 3 characters)');
    if (!description.trim() || description.trim().length < 20) return setSubmitError('Please provide more detail (at least 20 characters)');

    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/support/ticket`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ ticketType, subject: subject.trim(), description: description.trim() }),
      });
      const data = await res.json();
      if (data.ok) {
        setSubmitted(data.ticketId);
        setSubject('');
        setDescription('');
        setTicketType('');
      } else {
        setSubmitError(data.error || 'Failed to submit ticket');
      }
    } catch {
      setSubmitError('Network error');
    }
    setSubmitting(false);
  };

  const handleExportData = async () => {
    setExporting(true);
    try {
      const res = await fetch(`${API_URL}/api/account/export`, { credentials: 'include' });
      if (!res.ok) throw new Error('Export failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ehandshake-data-export-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch { /* silently fail */ }
    setExporting(false);
  };

  if (submitted) {
    return (
      <div className="flex flex-col gap-4">
        <h3 className="text-lg font-semibold text-gray-800">Support Ticket</h3>
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-6 text-center">
          <FaCheckCircle size={28} className="text-emerald-500 mx-auto mb-3" />
          <p className="text-sm font-semibold text-emerald-800">Ticket #{submitted} Submitted</p>
          <p className="text-xs text-emerald-600 mt-1">
            We've received your request and sent a notification to our support team. You'll hear back within 48 hours.
          </p>
          <button
            onClick={() => setSubmitted(null)}
            className="mt-4 px-4 py-1.5 text-xs font-medium text-[#00aef7] border border-[#00aef7] rounded-lg hover:bg-[#00aef7]/10 transition-colors"
          >
            Submit Another Ticket
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <h3 className="text-lg font-semibold text-gray-800">Support Ticket</h3>
      <p className="text-sm text-gray-600">
        Need help, want to report a bug, or have a request? Submit a ticket and our team will respond within 48 hours.
      </p>

      <div className="flex flex-col gap-3">
        {/* Ticket Type */}
        <div>
          <label className="block text-[11px] font-medium text-gray-600 mb-1">Ticket Type *</label>
          <select
            value={ticketType}
            onChange={(e) => { setTicketType(e.target.value); setSubmitError(''); }}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#00aef7]/30 focus:border-[#00aef7]"
          >
            <option value="">Select a type…</option>
            {TICKET_TYPES.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
        </div>

        {/* Data export prompt for delete account */}
        {ticketType === 'delete_account' && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
            <p className="text-xs text-amber-800 font-medium mb-2">
              Before requesting deletion, would you like to download a copy of your data?
            </p>
            <button
              onClick={handleExportData}
              disabled={exporting}
              className="inline-flex items-center gap-1.5 px-3 py-1 text-[11px] font-medium text-amber-700 border border-amber-300 rounded-lg hover:bg-amber-100 transition-colors disabled:opacity-50"
            >
              <FaDownload size={10} />
              {exporting ? 'Downloading…' : 'Download My Data'}
            </button>
          </div>
        )}

        {/* Subject */}
        <div>
          <label className="block text-[11px] font-medium text-gray-600 mb-1">Subject *</label>
          <input
            type="text"
            value={subject}
            onChange={(e) => { setSubject(e.target.value); setSubmitError(''); }}
            placeholder="Brief summary of your request"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#00aef7]/30 focus:border-[#00aef7]"
          />
        </div>

        {/* Description */}
        <div>
          <label className="block text-[11px] font-medium text-gray-600 mb-1">
            {ticketType === 'delete_account' ? 'Reason for deletion *' : 'Description *'}
          </label>
          <textarea
            value={description}
            onChange={(e) => { setDescription(e.target.value); setSubmitError(''); }}
            placeholder={ticketType === 'delete_account' ? 'Please tell us why you want to delete your account…' : 'Please describe your issue or request in detail…'}
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#00aef7]/30 focus:border-[#00aef7]"
          />
          <p className="text-[10px] text-gray-400 mt-0.5">Minimum 20 characters</p>
        </div>

        {submitError && <p className="text-xs text-red-500">{submitError}</p>}

        <button
          onClick={handleSubmit}
          disabled={submitting || !ticketType}
          className="w-full py-2 bg-[#00aef7] hover:bg-[#0095d6] text-white font-semibold rounded-lg transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {submitting ? 'Submitting…' : 'Submit Ticket'}
        </button>
      </div>
    </div>
  );
}

// ── FAQ Data ──────────────────────────────────────────────────────────────────
const FAQ_CATEGORIES = [
  {
    key: 'how-it-works',
    title: 'How eHandshake Works',
    iconImg: '/images/ehandshake-logo.svg',
    color: '#00aef7',
    questions: [
      {
        q: 'What is eHandshake?',
        a: 'eHandshake is a Collaborative Marketing Engine built for small businesses, tradespeople, and local professionals. Unlike traditional social media tools that just schedule your posts, eHandshake creates a system where teams of professionals actively promote each other to their own social media audiences — and actively support each other\'s content.\n\nHere\'s the core idea: you join a team of complementary businesses — say a plumber, an electrician, and a roofer. Each of you creates professional branded content in the Design Center, then shares each other\'s profiles, companies, and team cards to your own Facebook, Instagram, X, LinkedIn, YouTube, and TikTok audiences. Your plumber followers now see the electrician. The electrician\'s followers now see the roofer. Everyone\'s reach multiplies without spending a dollar on advertising.\n\nBut cross-promotion is only half the equation. When you post a creation to any social platform, it appears in the Sharing Center library for your entire team. Each teammate sees a direct link to your live post — they click through, land on your actual Facebook or Instagram post, and engage with it: likes, comments, shares. That early engagement from real people signals the platform\'s algorithm to show your content to more of your own followers. Your teammates aren\'t just promoting you to their audiences — they\'re boosting your content on yours.\n\neHandshake handles the entire workflow: AI-powered content creation, one-click social posting, branded profile cards, team click-through engagement, engagement tracking with milestone notifications, a daily digest email, and real-time team messaging — all in one place.'
      },
      {
        q: 'What is a "Collaborative Marketing Engine" and why does it matter?',
        a: 'A Collaborative Marketing Engine is a system that turns individual social media efforts into team-powered reach. Instead of marketing alone — where you post to your 200 followers and hope for the best — you become part of a team where every member\'s audience works for everyone else.\n\nIt works on two levels. First, your teammates share your profile and company to their audiences, introducing your business to people who\'ve never heard of you. Second — and this is just as important — when you post a creation to Facebook or Instagram, your teammates see it in the Sharing Center library, click through to the live post, and engage with it: likes, comments, shares. That organic team engagement tells the platform\'s algorithm that your content is worth showing to more people. Your post gets boosted not by paying for ads, but by real people on your team genuinely supporting each other\'s content.\n\nThink of it this way: if you have 500 followers across your social accounts, and your two teammates each have 500, your individual reach is 500. But when all three of you share each other\'s profiles AND actively engage with each other\'s posts, every person\'s content potentially reaches 1,500+ people. That\'s not theory — that\'s how eHandshake works in practice.\n\nThe "engine" part matters because eHandshake doesn\'t just connect you with teammates — it generates the content, renders the sharing cards, posts to the platforms, tracks the engagement, notifies your team when posts gain traction, and gives your teammates a direct link to click through and support every post. It runs the entire marketing cycle, not just one piece of it.'
      },
      {
        q: 'What are the main things I can do with eHandshake?',
        a: 'eHandshake has four core pillars that work together:\n\n1. Design Center — Create professional social media posts tailored to your profession. Search thousands of stock images and videos, select up to 10 at a time to compare, add AI-generated headlines and captions specific to your industry, and publish polished content in minutes — no design skills needed. For TikTok and YouTube, your images are automatically converted into cinematic Ken Burns videos.\n\n2. Sharing Center — Your command center for social distribution. Post your creations to any connected platform with one click. Once you post, your creation appears in the Sharing Center library where your teammates can see it, click through to the live post on that platform, and support you with likes, comments, and shares — boosting your reach and engagement organically. You also share your teammates\' profile cards, company cards, and team cards to your own social channels. Track engagement (likes, comments, views) in real time, and see everything your team has posted in a unified library view.\n\n3. Social Connections — Securely link your Facebook Page, Instagram Business account, X (Twitter), LinkedIn, YouTube, and TikTok accounts. eHandshake uses official OAuth — we never see your password. When you click "Post," the content goes directly to your connected account.\n\n4. Messaging — Built-in real-time messaging with direct messages and team group channels. Coordinate with teammates, discuss content ideas, and welcome new members without leaving the app.'
      },
      {
        q: 'How is eHandshake different from Buffer, Hootsuite, or Zoho Social?',
        a: 'Those tools are social media schedulers — you write your own content, schedule it to your own accounts, and look at your own analytics. They\'re solo tools.\n\neHandshake is fundamentally different in four ways:\n\nFirst, eHandshake creates the content for you. The Design Center provides AI-assisted headlines, industry-specific image search, professional templates, and automatic video generation. Buffer and Hootsuite assume you already have content ready to post.\n\nSecond, eHandshake is team-powered. Your teammates share YOUR profile to THEIR audiences, introducing your business to people you could never reach alone. No scheduler does this — it\'s not even a feature they offer because their model is built around individual accounts.\n\nThird, your team actively supports your content. When you post a creation, it shows up in the Sharing Center for your entire team. Each teammate can click "View on Facebook" (or Instagram, X, etc.) and land directly on your live post to like, comment, and share it. This organic engagement from real people signals the platform\'s algorithm to show your post to more people — something no scheduling tool provides.\n\nFourth, eHandshake tracks the full cycle. You don\'t just see "post sent." You see who on your team posted what, which posts are getting engagement, when milestones are hit (10 likes, 100 views), and you get a daily digest of everything your team accomplished. It\'s a collaborative marketing engine, not a posting queue.'
      },
      {
        q: 'Do I need a team to use eHandshake?',
        a: 'No — you can start as a Free Agent and get immediate value. Free Agents have full access to the Design Center to create professional social media content, and they can post their creations to any connected platform. If you just need a better way to create and publish social content for your business, eHandshake does that out of the box.\n\nBut the real multiplier kicks in when you join a team. As a Free Agent, you post to your own audience. As a team member, your teammates are actively sharing your profile and company to their audiences — people who\'ve never heard of you. You do the same for them. The result is that everyone on the team reaches 3x, 5x, or 10x the audience they could reach alone.\n\nThink of it this way: the Design Center is the car. The Sharing Center is the road. But your team is the fuel.'
      },
      {
        q: 'What social media platforms does eHandshake support?',
        a: 'eHandshake currently supports six major social platforms:\n\n• Facebook — Posts to your Facebook Page as photo posts with captions, hashtags, and profile links. Requires a Facebook Page (not a personal profile).\n\n• Instagram — Posts to your Instagram Business or Creator account. Your image is uploaded with a full caption and hashtags.\n\n• X (Twitter) — Posts as tweets with your image attached. Full v2 API integration.\n\n• LinkedIn — Posts to your LinkedIn personal profile with your image and professional caption.\n\n• YouTube — Automatically converts your image into a 12-second vertical Ken Burns video and uploads it as a YouTube Short.\n\n• TikTok — Same Ken Burns video conversion, uploaded directly to your TikTok account.\n\nAll connections use OAuth (the same "Sign in with..." flow you see on other sites). eHandshake never sees or stores your password. You authorize access once, and you can disconnect any platform at any time.'
      },
      {
        q: 'How does cross-promotion actually work?',
        a: 'Cross-promotion is the heart of eHandshake, and it works like this:\n\nLet\'s say you\'re a plumber named Paul, and your teammate Barbara is an optometrist. When you go to the Sharing Center and click on Barbara\'s profile in the Contacts section, eHandshake generates a professional branded card featuring Barbara\'s photo, business name, industry, star rating, and a link to her public profile. You write a short caption — something like "Need an eye exam? My colleague Dr. Barbara Bush is excellent" — and click Post to Facebook.\n\nThat card, with YOUR endorsement, goes out to YOUR Facebook audience. Your followers see a trusted recommendation from someone they already follow. Barbara gets exposure to an audience she could never reach on her own.\n\nBarbara does the same thing for you — she shares your plumbing profile to her Instagram audience. Her followers now know a good plumber.\n\nThis is not spam. This is not automated. Every share is intentional, written in your voice, posted to your own account. eHandshake enforces a 30-day cooldown per person per platform so your feed never looks repetitive. The result is authentic, personal referrals at social media scale.'
      },
      {
        q: 'Is there a limit to how often I can post?',
        a: 'For your own creations (content you designed in the Design Center), there is no posting frequency limit. You can post the same creation to Facebook, Instagram, X, LinkedIn, YouTube, and TikTok as many times as you like.\n\nFor sharing teammates\' profiles, companies, and team cards, there is a 30-day cooldown per target, per platform. This means: if you share Barbara\'s profile to Facebook today, you can\'t share Barbara\'s profile to Facebook again for 30 days. But you CAN share Barbara to Instagram, X, or any other platform immediately. And you can share a different teammate to Facebook right away.\n\nThis cooldown exists to protect your social media accounts. Platforms like Facebook and Instagram use spam detection algorithms that flag repetitive promotional content. The 30-day window ensures your sharing activity looks natural and authentic — because it is.'
      },
      {
        q: 'What happens when someone sees and clicks on my social media post?',
        a: 'When someone in your social media audience sees your eHandshake post and clicks on it, they land on the live post on that platform (Facebook, Instagram, etc.). From there they can like, comment, share, and follow you — all the normal social engagement that builds your online presence.\n\neHandshake automatically includes your public profile link and your company website in the caption of every post, so interested viewers can click through to learn more about your business on ehandshake.com or your own website.\n\nBut it\'s not just strangers who engage. When you post a creation, it immediately appears in the Sharing Center library for your entire team. Your teammates see a green "View on Facebook" (or whichever platform) button, click it, and land directly on your live post. They like it, leave a comment, share it to their own feed — genuine engagement from real people who want to see you succeed. This team engagement is incredibly valuable because social media algorithms prioritize content that gets early interaction. When your teammates engage in the first few hours, the platform shows your post to more of your own followers too.\n\nBehind the scenes, eHandshake tracks engagement on every post. Likes, comments, and views are polled automatically and displayed in the Sharing Center. When your post hits milestone numbers — your first like, 10 likes, 50 likes, 100 views — your team\'s activity feed is notified so everyone can celebrate the win. This isn\'t vanity metrics — it\'s validation that the collaborative marketing engine is working.'
      },
    ],
  },
  {
    key: 'teams',
    title: 'Teams & Membership',
    icon: FaUsers,
    color: '#10B981',
    questions: [
      { q: 'Why should I join a team?', a: 'Teams unlock the core value of eHandshake: cross-promotion. When you\'re on a team, your teammates share your profile and company to their social media audiences — people you could never reach on your own. In return, you share theirs. It\'s collaborative marketing that multiplies everyone\'s reach.' },
      { q: 'How do I create a team?', a: 'Open the Team panel from the left navigation bar, then click "Create Team." Name your team, and you\'ll become the Captain. You can then invite teammates by searching for their name or sending an invite link.' },
      { q: 'What does a Captain do?', a: 'The Captain is the team founder and administrator. They can invite new members, manage the roster, and set the team\'s public profile. Captains get unlimited creations as long as the team has 3 or more active members.' },
      { q: 'What happens if my team drops below 3 members?', a: 'If you\'re the Captain, you\'ll receive an email notification and have 30 days to recruit back to 3 members. During this grace period, everything stays the same. If 30 days pass without reaching 3 members, your creation limit adjusts to First String levels (5 active creations). Your existing content is preserved.' },
      { q: 'Can I be on multiple teams?', a: 'Yes. You can be a member of multiple teams simultaneously. Each team operates independently — you can share different teammates\' profiles to different audiences depending on which team they\'re on.' },
      { q: 'How do I leave a team?', a: 'Open the Team panel, find the team you want to leave, and click "Leave Team." Your shared content stays live, but you\'ll no longer appear in that team\'s roster or Sharing Center.' },
      { q: 'What\'s the difference between a team member and a Free Agent?', a: 'Free Agents work solo — they can create content and post to their own channels, but nobody is cross-promoting them. Team members get the full collaborative experience: teammates share your profile, you share theirs, and everyone\'s social reach grows together.' },
      { q: 'Can anyone share any team?', a: 'Yes. Any eHandshake user can share any team\'s card to their social media. You don\'t need to be a member of that team to promote it.' },
    ],
  },
  {
    key: 'subscriptions',
    title: 'Subscriptions & Pricing',
    icon: FaCreditCard,
    color: '#8B5CF6',
    questions: [
      { q: 'What are the subscription tiers?', a: 'Free Agent ($0) — 1 active creation, post to your own channels. First String ($19.99/mo) — 5 active creations. Team Member ($49.99/mo) — unlimited creations, full team cross-promotion. Captain ($19.99/mo) — same as Team Member with unlimited creations while the team has 3+ members.' },
      { q: 'Is there an annual discount?', a: 'Yes. Annual plans save roughly 10% compared to paying monthly. Annual pricing: First String $215.89/year, Team Member $539.89/year.' },
      { q: 'What does "active creations" mean?', a: 'An active creation is a published post in your Design Center library. Free Agents can have 1 active creation at a time, First String can have 5. If you hit your limit, you can archive an older creation to make room for a new one.' },
      { q: 'How do I upgrade my plan?', a: 'Click the upgrade button in the Design Center header, the Sharing Center, or the Help Center subscription panel. You\'ll be taken to a secure checkout overlay where you can choose your plan and enter payment info.' },
      { q: 'Can I downgrade?', a: 'Yes. You can change your plan at any time through the subscription portal. If you downgrade, the change takes effect at the end of your current billing period. Your creations are preserved but only the most recent ones (up to your new limit) will be visible.' },
      { q: 'What payment methods are accepted?', a: 'We accept all major credit cards through our secure payment processor. All billing, tax calculation, and receipts are handled automatically.' },
      { q: 'How do I cancel?', a: 'Open the subscription portal from the Help Center and click "Cancel Subscription." Your access continues until the end of your current billing period. You can resubscribe at any time.' },
      { q: 'Do I get a refund if I cancel mid-cycle?', a: 'Your subscription remains active until the end of the current billing period. We don\'t offer partial refunds, but you\'ll have full access for the time you\'ve already paid for.' },
    ],
  },
  {
    key: 'design-center',
    title: 'Design Center & Creations',
    iconSvg: (color) => (
      <svg xmlns="http://www.w3.org/2000/svg" height="16px" viewBox="0 -960 960 960" width="16px" fill={color}><path d="M240-120q-45 0-89-22t-71-58q26 0 53-20.5t27-59.5q0-50 35-85t85-35q50 0 85 35t35 85q0 66-47 113t-113 47Zm0-80q33 0 56.5-23.5T320-280q0-17-11.5-28.5T280-320q-17 0-28.5 11.5T240-280q0 23-5.5 42T220-202q5 2 10 2h10Zm230-160L360-470l358-358q11-11 27.5-11.5T774-828l54 54q12 12 12 28t-12 28L470-360Zm-190 80Z"/></svg>
    ),
    color: '#F59E0B',
    questions: [
      { q: 'How do I create a new post?', a: 'Open the Design Center from the left navigation bar. Choose a content category (How Things Work, How-To & Tips, etc.), search for images or videos, select one or more, then add your headline and caption. When you\'re happy with the design, click Publish.' },
      { q: 'Can I select multiple images at once?', a: 'Yes! In the image or video search modal, you can select up to 10 items at once. They\'ll all be added to your library, and the first one will automatically appear on the canvas. Click between them in the left panel to compare which looks best.' },
      { q: 'What are the content categories for?', a: 'Categories help organize your content strategy around proven goals: educating your audience (How Things Work, How-To & Tips), building trust (Problems & Solutions, Advice & Insights), promoting offers (Specials & Discounts), boosting engagement (Stories & Experiences, Community Relations), and showing personality (Humor & Personality). AI suggestions are tailored to your category and profession.' },
      { q: 'How does AI assistance work?', a: 'After selecting an image and category, the AI generates headline suggestions and captions tailored to your profession and industry. You can use them as-is, modify them, or write your own from scratch.' },
      { q: 'What image/video formats are supported?', a: 'The Design Center uses high-quality stock images from our built-in library. For TikTok and YouTube, your image is automatically converted into a 12-second Ken Burns video (vertical 9:16 format) with smooth pan and zoom animation.' },
      { q: 'Can I use my own images?', a: 'Yes. You can upload your own images directly in the Design Center alongside the stock library search.' },
      { q: 'What is a Ken Burns video?', a: 'When you post to TikTok or YouTube, eHandshake automatically generates a 12-second vertical video from your image using the Ken Burns effect — a cinematic slow pan and zoom that brings still images to life. Your headline is overlaid on the video.' },
      { q: 'How do I edit a creation after publishing?', a: 'Open the Sharing Center, find your creation, and click it to open the preview. From there you can modify the caption or headline. The image itself is locked after publishing to maintain consistency across platforms.' },
    ],
  },
  {
    key: 'sharing-center',
    title: 'Sharing Center & Social Posting',
    iconSvg: (color) => (
      <svg xmlns="http://www.w3.org/2000/svg" height="16px" viewBox="0 -960 960 960" width="16px" fill={color}><path d="M680-80q-50 0-85-35t-35-85q0-6 3-28L282-392q-16 15-37 23.5t-45 8.5q-50 0-85-35t-35-85q0-50 35-85t85-35q24 0 45 8.5t37 23.5l281-164q-2-7-2.5-13.5T560-760q0-50 35-85t85-35q50 0 85 35t35 85q0 50-35 85t-85 35q-24 0-45-8.5T598-672L317-508q2 7 2.5 13.5t.5 14.5q0 8-.5 14.5T317-452l281 164q16-15 37-23.5t45-8.5q50 0 85 35t35 85q0 50-35 85t-85 35Z"/></svg>
    ),
    color: '#EF4444',
    questions: [
      { q: 'What is the Sharing Center?', a: 'The Sharing Center is your command center for social media distribution. It shows all published creations in a library view, lets you post to any connected platform, and tracks engagement (likes, comments, views) on every post you\'ve made.' },
      { q: 'How do I post a creation to social media?', a: 'Open the Sharing Center, click a platform icon at the top (Facebook, Instagram, etc.), find the creation you want to post, click it to open the preview, then click "Post to [Platform]." The post is made immediately to your connected account.' },
      { q: 'What does the "View on Platform" button do?', a: 'When a creation has already been posted to a platform, a green "View on [Platform]" button appears in the preview. Clicking it opens the live post on that social media platform in a new tab so you can see engagement, respond to comments, and share the link.' },
      { q: 'How does the 30-day cooldown work?', a: 'When you share a teammate\'s profile, company, or team card to a social platform, there\'s a 30-day cooldown before you can share that same person to that same platform again. This prevents spam and protects your social media accounts from being flagged. You can still share them to a different platform immediately.' },
      { q: 'Why is a card grayed out in the Sharing Center?', a: 'A grayed-out card means one of three things: (1) you already shared this profile/company/team to the selected platform within the last 30 days, (2) the person has opted out of being shared, or (3) it\'s your own profile. Hover over the card for a detailed explanation.' },
      { q: 'What are engagement counts?', a: 'eHandshake automatically polls each platform for likes, comments, and views on your posts. These counts appear as small badges under the "View on [Platform]" button. When your post hits milestone numbers (10 likes, 100 views, etc.), your team\'s activity feed is notified.' },
      { q: 'What is the daily digest email?', a: 'Team members receive a daily email at 11:59 PM local time summarizing everything their teammates posted in the last 24 hours, grouped by platform. Each entry has a "View on [Platform]" button that takes you directly to the live post. If nothing was posted that day, no email is sent.' },
      { q: 'Do Free Agent posts expire?', a: 'Yes. Free Agent creations expire from the Sharing Center library after 7 days. Paid tier creations (First String, Team Member, Captain) never expire.' },
    ],
  },
  {
    key: 'social-connections',
    title: 'Social Media Connections',
    icon: FaHandshake,
    color: '#0EA5E9',
    questions: [
      { q: 'How do I connect a social media account?', a: 'In the Sharing Center, click the platform icon you want to connect (Facebook, Instagram, etc.). If you\'re not connected yet, you\'ll be prompted to authorize eHandshake through that platform\'s official OAuth flow. You\'ll log in on the platform\'s own site — eHandshake never sees your password.' },
      { q: 'What permissions does eHandshake need?', a: 'Each platform requires specific permissions to post on your behalf: Facebook needs Page posting access, Instagram needs Business/Creator account access, X needs tweet creation access, YouTube needs video upload access, and TikTok needs video publishing access. eHandshake only uses these permissions to post content you explicitly initiate.' },
      { q: 'Can eHandshake post without my permission?', a: 'No. eHandshake never posts automatically. Every single post is initiated by you — you click the "Post to [Platform]" button, write or approve the caption, and confirm. There are no scheduled or automatic posts.' },
      { q: 'What if my connection expires?', a: 'Social media tokens expire periodically (each platform sets its own timeline). When a token expires, eHandshake will automatically disconnect that platform and you\'ll see a prompt to reconnect. Simply click the platform icon and re-authorize.' },
      { q: 'How do I disconnect a platform?', a: 'Go to the Sharing Center, click the connected platform icon, and choose "Disconnect." Your past posts remain live on the platform — disconnecting only removes eHandshake\'s ability to post new content.' },
      { q: 'Do I need a Facebook Page or can I use a personal profile?', a: 'Facebook requires a Page for posting through third-party apps like eHandshake. If you don\'t have a Page yet, you can create one for free at facebook.com/pages/create. Personal profiles are not supported by Facebook\'s API.' },
      { q: 'Why does Instagram require a Business or Creator account?', a: 'Instagram\'s API only allows third-party posting to Business and Creator accounts. You can switch your personal Instagram to a Business account for free in Instagram\'s settings — it takes about 30 seconds and doesn\'t affect your existing content.' },
      { q: 'Can I connect multiple accounts per platform?', a: 'Currently, eHandshake supports one active connection per platform per user. If you want to switch to a different account, disconnect the current one and connect the new one.' },
    ],
  },
  {
    key: 'profile-setup',
    title: 'Profile & Business Setup',
    icon: FaUser,
    color: '#6366F1',
    questions: [
      { q: 'How do I set up my profile?', a: 'Click your avatar or name in the top navigation bar to open your profile. Fill in your name, business name, industry, location, phone number, and upload a profile photo and company logo. The more complete your profile, the better your sharing cards look when teammates promote you.' },
      { q: 'What is a public profile URL?', a: 'Every eHandshake user gets a public profile page at ehandshake.com/userprofiles/users/your-url-name. This URL is automatically included in your social media posts so people can learn more about you.' },
      { q: 'How do I import Google Reviews?', a: 'Go to your profile and find the Google Reviews section. Click "Import from Google" and search for your business. Select your listing and eHandshake will pull in your 5 most recent reviews. You can refresh reviews every 30 days.' },
      { q: 'What is profession resolution?', a: 'When you first open the Design Center, eHandshake asks about your industry or profession. This is used to generate relevant AI content suggestions, search for industry-specific stock images, and categorize your profile in the public directory.' },
      { q: 'Can I update my profile photo and logo?', a: 'Yes, at any time. Go to your profile settings and upload new images. The updated photo/logo will automatically appear on all future sharing cards and your public profile.' },
      { q: 'What information appears on my sharing card?', a: 'When a teammate shares your profile, the card includes your name, business name, industry, location, profile photo, company logo, star rating (if you have Google Reviews), and a link to your public profile page.' },
      { q: 'How do I add my website?', a: 'Go to your business profile settings and enter your website URL. This will be included in your sharing cards and public profile. Your website link appears in the caption of every social media post made about your business.' },
      { q: 'Can I opt out of being shared?', a: 'Yes. In the Help Center under Privacy, toggle the sharing opt-out checkbox. This prevents anyone from sharing your profile or company card on social media. You can re-enable sharing at any time.' },
    ],
  },
  {
    key: 'privacy',
    title: 'Privacy & Account',
    icon: FaShieldAlt,
    color: '#64748B',
    questions: [
      { q: 'What data does eHandshake collect?', a: 'eHandshake stores the information you provide (name, business info, profile photo) plus your social media connection tokens (encrypted, never your password). We track which posts you make and their engagement metrics. We do not sell or share your data with third parties.' },
      { q: 'How do I opt out of being shared?', a: 'Open the Help Center, go to Privacy, and toggle the sharing opt-out. When enabled, no one can share your profile or company card to social media. Your own creations are not affected — you can still post your own content.' },
      { q: 'Can my teammates see my social media passwords?', a: 'No. eHandshake uses OAuth, which means you authorize access through each platform\'s own login page. We receive a token — never your password. Teammates cannot see your connections, tokens, or any private account information.' },
      { q: 'What can my teammates see about me?', a: 'Teammates can see your public profile: name, business name, industry, location, profile photo, logo, and Google Reviews. They can also see your shared creations in the Sharing Center library. They cannot see your social media connections, private messages with other users, or account settings.' },
      { q: 'How do I delete my account?', a: 'Contact support@ehandshake.com and request account deletion. We will process your request within 30 days. All personal data, social media tokens, and account connections will be permanently removed.' },
      { q: 'How do I contact support?', a: 'Open the Help Center and click "Support Ticket" in the left sidebar. Fill out the form with your issue type and description. You can also email support@ehandshake.com directly.' },
      { q: 'Is my payment information secure?', a: 'eHandshake never stores your credit card number. All payment processing is handled by our secure Merchant of Record, which is PCI-DSS compliant. We only receive confirmation of your subscription status.' },
      { q: 'How do I change my password?', a: 'Open the Help Center, go to Account, and use the "Change Password" section. Enter your current password and your new password. Passwords must meet the minimum strength requirements shown by the meter.' },
    ],
  },
  {
    key: 'messaging',
    title: 'Messaging',
    icon: FaComments,
    color: '#EC4899',
    questions: [
      { q: 'How does messaging work?', a: 'eHandshake has a built-in real-time messaging system. You can send direct messages to any user and participate in team group channels. Messages are delivered instantly and persist across sessions.' },
      { q: 'How do I start a conversation?', a: 'Click the messaging icon in the left navigation bar, then click "New Message." Search for a user by name and start typing. You can also click on a teammate\'s profile card and choose "Message" to start a DM.' },
      { q: 'What are team channels?', a: 'Every team automatically gets a group channel where all members can communicate. This is great for coordinating sharing schedules, discussing content ideas, and welcoming new members.' },
      { q: 'Can I message people outside my team?', a: 'Yes. You can send a direct message to any eHandshake user, not just your teammates. The messaging system is platform-wide.' },
      { q: 'Are my messages private?', a: 'Direct messages are private between you and the recipient. Team channel messages are visible to all active members of that team. eHandshake does not read, scan, or share your messages.' },
      { q: 'Is there a global chat room?', a: 'Yes. There\'s a global "Everyone" channel where all eHandshake users can interact. This is a good place to find potential teammates, ask questions, and engage with the community.' },
      { q: 'Do I get notified of new messages?', a: 'Yes. Unread message counts appear as badges on the messaging icon. When you open the messaging panel, conversations with unread messages are highlighted.' },
      { q: 'Can I send images or files in messages?', a: 'Currently, messaging supports text-only communication. File and image sharing in messages is planned for a future update.' },
    ],
  },
];

function FAQPanel() {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState(null);
  const [openQuestion, setOpenQuestion] = useState(null); // 'categoryKey:index'

  // Filter questions by search query
  const filteredCategories = searchQuery.trim()
    ? FAQ_CATEGORIES.map(cat => ({
        ...cat,
        questions: cat.questions.filter(qa =>
          qa.q.toLowerCase().includes(searchQuery.toLowerCase()) ||
          qa.a.toLowerCase().includes(searchQuery.toLowerCase())
        ),
      })).filter(cat => cat.questions.length > 0)
    : activeCategory
      ? FAQ_CATEGORIES.filter(cat => cat.key === activeCategory)
      : FAQ_CATEGORIES;

  const totalMatches = searchQuery.trim()
    ? filteredCategories.reduce((sum, cat) => sum + cat.questions.length, 0)
    : null;

  return (
    <div className="flex flex-col gap-4">
      <h3 className="text-lg font-semibold text-gray-800">Frequently Asked Questions</h3>

      {/* Search bar */}
      <div className="relative">
        <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={13} />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => { setSearchQuery(e.target.value); setActiveCategory(null); }}
          placeholder="Search all questions..."
          className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:border-[#00aef7] focus:ring-1 focus:ring-[#00aef7]/30"
        />
        {searchQuery && (
          <button
            onClick={() => setSearchQuery('')}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            <FaTimes size={12} />
          </button>
        )}
      </div>

      {/* Search results count */}
      {totalMatches !== null && (
        <p className="text-xs text-gray-500">
          {totalMatches === 0 ? 'No questions match your search' : `${totalMatches} question${totalMatches === 1 ? '' : 's'} found`}
        </p>
      )}

      {/* Category cards grid — hidden during search */}
      {!searchQuery.trim() && !activeCategory && (
        <div className="grid grid-cols-3 gap-2">
          {FAQ_CATEGORIES.map(cat => {
            const Icon = cat.icon;
            return (
              <button
                key={cat.key}
                onClick={() => setActiveCategory(cat.key)}
                className="flex flex-col items-center gap-1.5 p-3 rounded-xl border border-gray-200 hover:border-[#00aef7] hover:shadow-md transition-all text-center group"
              >
                <div
                  className="w-9 h-9 rounded-full flex items-center justify-center transition-colors"
                  style={{ backgroundColor: cat.color + '18' }}
                >
                  {cat.iconImg ? (
                    <img src={cat.iconImg} alt="" className="w-5 h-5 object-contain" />
                  ) : cat.iconSvg ? (
                    cat.iconSvg(cat.color)
                  ) : (
                    <Icon size={16} style={{ color: cat.color }} />
                  )}
                </div>
                <span className="text-[11px] font-semibold text-gray-700 leading-tight group-hover:text-[#00aef7]">{cat.title}</span>
                <span className="text-[10px] text-gray-400">{cat.questions.length} questions</span>
              </button>
            );
          })}
        </div>
      )}

      {/* Back button when viewing a single category */}
      {activeCategory && !searchQuery.trim() && (
        <button
          onClick={() => { setActiveCategory(null); setOpenQuestion(null); }}
          className="flex items-center gap-1.5 text-xs text-[#00aef7] hover:text-[#0097d6] font-medium"
        >
          <FaArrowLeft size={10} />
          All categories
        </button>
      )}

      {/* Question accordions */}
      <div className="flex flex-col gap-3">
        {filteredCategories.map(cat => {
          const Icon = cat.icon;
          return (
            <div key={cat.key} className="rounded-xl border border-gray-200 overflow-hidden">
              {/* Category header */}
              <div
                className="flex items-center gap-2.5 px-4 py-2.5"
                style={{ backgroundColor: cat.color + '0C', borderBottom: `2px solid ${cat.color}30` }}
              >
                <div
                  className="w-7 h-7 rounded-full flex items-center justify-center shrink-0"
                  style={{ backgroundColor: cat.color + '20' }}
                >
                  {cat.iconImg ? (
                    <img src={cat.iconImg} alt="" className="w-4 h-4 object-contain" />
                  ) : cat.iconSvg ? (
                    cat.iconSvg(cat.color)
                  ) : (
                    <Icon size={13} style={{ color: cat.color }} />
                  )}
                </div>
                <span className="text-sm font-semibold text-gray-800">{cat.title}</span>
                {searchQuery.trim() && (
                  <span className="ml-auto text-[10px] text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">{cat.questions.length}</span>
                )}
              </div>

              {/* Questions */}
              <div className="divide-y divide-gray-100">
                {cat.questions.map((qa, idx) => {
                  const qKey = `${cat.key}:${idx}`;
                  const isOpen = openQuestion === qKey;
                  return (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => setOpenQuestion(isOpen ? null : qKey)}
                      className="w-full text-left px-4 py-2.5 hover:bg-gray-50/80 transition-colors flex flex-col"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <span className={`text-[13px] leading-snug ${isOpen ? 'font-semibold text-gray-900' : 'font-medium text-gray-700'}`}>
                          {qa.q}
                        </span>
                        <svg
                          className={`w-3.5 h-3.5 shrink-0 mt-0.5 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                      </div>
                      {isOpen && (
                        <div
                          className="mt-2 text-xs text-gray-600 leading-relaxed pl-0 pr-4 pb-1"
                          style={{ borderLeft: `3px solid ${cat.color}40` }}
                        >
                          <div className="pl-3 whitespace-pre-line">{qa.a}</div>
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function LegalPanel() {
  return (
    <div className="flex flex-col gap-4">
      <h3 className="text-lg font-semibold text-gray-800">Legal</h3>
      <div className="flex flex-col gap-3">
        <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-sm text-[#00aef7] hover:underline font-medium">
          Terms of Service
        </a>
        <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-sm text-[#00aef7] hover:underline font-medium">
          Privacy Policy
        </a>
        <a href="/cookies" target="_blank" rel="noopener noreferrer" className="text-sm text-[#00aef7] hover:underline font-medium">
          Cookie Policy
        </a>
      </div>
    </div>
  );
}

// ── Reusable row component for settings items ──
function SectionRow({ label, description, comingSoon, danger }) {
  return (
    <div className="flex items-center justify-between p-3 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors">
      <div className="flex-1 min-w-0">
        <p className={`text-sm font-medium ${danger ? 'text-red-600' : 'text-gray-800'}`}>{label}</p>
        {description && <p className="text-xs text-gray-500 mt-0.5">{description}</p>}
      </div>
      {comingSoon ? (
        <span className="ml-3 shrink-0 px-2 py-0.5 text-[10px] font-semibold rounded-full bg-gray-100 text-gray-500 uppercase tracking-wide">
          Soon
        </span>
      ) : (
        <button className={`ml-3 shrink-0 px-3 py-1 text-xs font-semibold rounded-lg ${
          danger
            ? 'bg-red-50 text-red-600 hover:bg-red-100'
            : 'bg-[#00aef7] text-white hover:bg-[#0098d8]'
        } transition-colors`}>
          Open
        </button>
      )}
    </div>
  );
}

// ── Main component ──
export default function HelpCenter() {
  const { helpCenterOpen, closeHelpCenter } = useModalStore();
  const bringToFront = useModalStore((s) => s.bringToFront);
  const helpCenterZ = useModalStore((s) => s.getZIndex('helpCenter'));
  const resetWalkthrough = useModalStore((s) => s.resetWalkthrough);
  const helpCenterPreSelect = useModalStore((s) => s.helpCenterPreSelect);
  const clearHelpCenterPreSelect = useModalStore((s) => s.clearHelpCenterPreSelect);
  const profile = useProfileStore((s) => s.profile);

  const [activeSection, setActiveSection] = useState('ai');
  const [pendingTicketType, setPendingTicketType] = useState(null);
  const [pendingTicketSubject, setPendingTicketSubject] = useState('');
  const [windowMode, setWindowMode] = useState('normal');
  const [windowRect, setWindowRect] = useState(null);
  const modalRef = useRef(null);

  const isOwner = (profile?.u_role || '').toLowerCase() === 'owner';
  // Captain detection: for now treat owners as captains; real captain logic
  // will use team membership data when Team Management is wired up.
  const isCaptain = isOwner;

  const getNormalRect = useCallback(() => {
    const padding = 16;
    const TARGET_WIDTH = 820;
    const MIN_HEIGHT = 520;

    const w = Math.min(TARGET_WIDTH, window.innerWidth - padding * 2);
    const h = Math.max(
      MIN_HEIGHT,
      Math.min(Math.floor(window.innerHeight * 0.82), window.innerHeight - padding * 2)
    );

    let x = Math.floor((window.innerWidth - w) / 2);
    let y = Math.floor((window.innerHeight - h) / 2);
    x = Math.max(padding, Math.min(x, window.innerWidth - w - padding));
    y = Math.max(padding, Math.min(y, window.innerHeight - h - padding));

    return { width: w, height: h, x, y };
  }, []);

  const getMaxRect = useCallback(() => {
    const pad = 12;
    return {
      x: pad,
      y: pad,
      width: Math.max(600, window.innerWidth - pad * 2),
      height: Math.max(400, window.innerHeight - pad * 2),
    };
  }, []);

  const getSidebarRect = useCallback(() => {
    const pad = 12;
    return {
      x: pad,
      y: pad,
      width: 340,
      height: Math.max(400, window.innerHeight - pad * 2),
    };
  }, []);

  // Store the rect before snapping so we can restore it
  const preSidebarRect = useRef(null);

  const snapToSidebar = useCallback(() => {
    preSidebarRect.current = windowRect || getNormalRect();
    setWindowMode('sidebar');
    setWindowRect(getSidebarRect());
  }, [windowRect, getNormalRect, getSidebarRect]);

  const restoreFromSidebar = useCallback(() => {
    setWindowMode('normal');
    setWindowRect(preSidebarRect.current || getNormalRect());
    preSidebarRect.current = null;
  }, [getNormalRect]);

  // Reset to safe position on each open; consume preSelect if set
  useEffect(() => {
    if (!helpCenterOpen) return;
    setWindowMode('normal');
    setWindowRect(getNormalRect());
    preSidebarRect.current = null;
    // If opened with a preselected section (e.g. from external trigger)
    if (helpCenterPreSelect) {
      setActiveSection(helpCenterPreSelect.section || 'ai');
      if (helpCenterPreSelect.data?.ticketType) {
        setPendingTicketType(helpCenterPreSelect.data.ticketType);
      }
      clearHelpCenterPreSelect();
    }
  }, [helpCenterOpen, getNormalRect, helpCenterPreSelect, clearHelpCenterPreSelect]);

  // Prevent background scroll while keeping the scrollbar visible (no layout shift)
  useEffect(() => {
    if (!helpCenterOpen) return;
    const onWheel = (e) => {
      // Allow scrolling inside the help center itself
      if (e.target.closest('.eh-help-nodrag') || e.target.closest('.eh-help-header')) return;
      e.preventDefault();
    };
    const onTouchMove = (e) => {
      if (e.target.closest('.eh-help-nodrag') || e.target.closest('.eh-help-header')) return;
      e.preventDefault();
    };
    const onKeyDown = (e) => {
      // Block Page Up/Down, Space, Arrow keys on background
      if (['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' '].includes(e.key)) {
        if (!e.target.closest('.eh-help-nodrag')) {
          e.preventDefault();
        }
      }
    };
    document.addEventListener('wheel', onWheel, { passive: false });
    document.addEventListener('touchmove', onTouchMove, { passive: false });
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('wheel', onWheel);
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [helpCenterOpen]);

  const toggleMaximize = () => {
    if (windowMode === 'max') {
      setWindowMode('normal');
    } else {
      setWindowMode('max');
    }
  };

  if (!helpCenterOpen) return null;
  if (!windowRect) return null;

  // Cross-panel navigation: AccountPanel → SupportTicketPanel
  const handleOpenTicket = (ticketType, ticketSubject) => {
    setPendingTicketType(ticketType);
    setPendingTicketSubject(ticketSubject || '');
    setActiveSection('support');
  };

  // Clear pending ticket type when leaving the support section
  const handleSectionChange = (key) => {
    if (key !== 'support') { setPendingTicketType(null); setPendingTicketSubject(''); }
    setActiveSection(key);
  };

  // Render the active panel
  const renderPanel = () => {
    switch (activeSection) {
      case 'ai':            return <AIHelpPanel snapToSidebar={snapToSidebar} restoreFromSidebar={restoreFromSidebar} />;
      case 'account':       return <AccountPanel onOpenTicket={handleOpenTicket} />;
      case 'subscription':  return <SubscriptionPanel />;
      case 'team':          return <TeamManagementPanel isCaptain={isCaptain} />;
      case 'privacy':       return <PrivacyPanel />;
      case 'faq':           return <FAQPanel />;
      case 'support':       return <SupportTicketPanel preSelectedType={pendingTicketType} preFilledSubject={pendingTicketSubject} />;
      case 'legal':         return <LegalPanel />;
      default:              return <AIHelpPanel snapToSidebar={snapToSidebar} restoreFromSidebar={restoreFromSidebar} />;
    }
  };

  return (
    <div
      style={{ zIndex: helpCenterZ }}
      className="fixed inset-0 flex items-center justify-center pointer-events-none"
      aria-modal="true"
      role="dialog"
      aria-label="Help Center"
    >
      <Rnd
        ref={modalRef}
        cancel=".eh-help-nodrag"
        onMouseDownCapture={() => bringToFront('helpCenter')}
        size={
          windowMode === 'max'
            ? { width: getMaxRect().width, height: getMaxRect().height }
            : windowMode === 'sidebar'
            ? { width: getSidebarRect().width, height: getSidebarRect().height }
            : { width: windowRect.width, height: windowRect.height }
        }
        position={
          windowMode === 'max'
            ? { x: getMaxRect().x, y: getMaxRect().y }
            : windowMode === 'sidebar'
            ? { x: getSidebarRect().x, y: getSidebarRect().y }
            : { x: windowRect.x, y: windowRect.y }
        }
        minWidth={windowMode === 'sidebar' ? 300 : 520}
        minHeight={400}
        bounds="window"
        dragHandleClassName="eh-help-header"
        disableDragging={windowMode === 'max' || windowMode === 'sidebar'}
        enableResizing={
          windowMode === 'max' || windowMode === 'sidebar'
            ? false
            : {
                bottomRight: true,
                bottomLeft: true,
                topRight: true,
                topLeft: true,
                left: true,
                right: true,
                bottom: true,
                top: true,
              }
        }
        onDragStop={(e, d) => {
          if (windowMode === 'max') return;
          setWindowRect((prev) => ({ ...(prev || {}), x: d.x, y: d.y }));
        }}
        onResizeStop={(e, direction, ref, delta, position) => {
          if (windowMode === 'max') return;
          setWindowRect({
            width: ref.offsetWidth,
            height: ref.offsetHeight,
            x: position.x,
            y: position.y,
          });
        }}
        className="rounded-2xl shadow-2xl overflow-hidden bg-white pointer-events-auto"
        style={{ height: '100%' }}
      >
        <div className="flex flex-col h-full">
        {/* ── Header ── */}
        <div className="shrink-0 flex items-center justify-between px-4 sm:px-6 py-2 border-b border-[#00aef7] bg-[#00aef7] text-white cursor-move eh-help-header select-none">
          <h2 className="text-base sm:text-lg font-semibold">Help Center</h2>
          <div className="flex items-center gap-1">
            {windowMode === 'sidebar' ? (
              <button
                onClick={restoreFromSidebar}
                className="w-7 h-7 flex items-center justify-center rounded hover:bg-white/20 transition-colors eh-help-nodrag"
                title="Restore full size"
              >
                <FaWindowRestore size={12} />
              </button>
            ) : (
              <button
                onClick={toggleMaximize}
                className="w-7 h-7 flex items-center justify-center rounded hover:bg-white/20 transition-colors eh-help-nodrag"
                title={windowMode === 'max' ? 'Restore' : 'Maximize'}
              >
                {windowMode === 'max' ? <FaWindowRestore size={12} /> : <FaWindowMaximize size={12} />}
              </button>
            )}
            <button
              onClick={() => { resetWalkthrough(); preSidebarRect.current = null; closeHelpCenter(); }}
              className="w-7 h-7 flex items-center justify-center rounded hover:bg-white/20 transition-colors eh-help-nodrag"
              title="Close"
            >
              <FaTimes size={14} />
            </button>
          </div>
        </div>

        {/* ── Body: sidebar + content ── */}
        <div className="flex flex-1 min-h-0 overflow-hidden">
          {/* Left sidebar — hidden in sidebar mode to maximize content space */}
          <nav className={`w-[200px] shrink-0 border-r border-gray-200 bg-gray-50 overflow-y-auto eh-help-nodrag ${windowMode === 'sidebar' ? 'hidden' : ''}`}>
            <ul className="py-2">
              {SECTIONS.map((sec) => {
                const Icon = sec.icon;
                // Gray out team management for non-captains (but still show it)
                const disabled = sec.captainOnly && !isCaptain;

                return (
                  <li key={sec.key}>
                    <button
                      onClick={() => handleSectionChange(sec.key)}
                      className={`w-full flex items-center gap-2.5 px-4 py-2.5 text-sm font-medium transition-colors text-left
                        ${activeSection === sec.key
                          ? 'bg-[#00aef7]/10 text-[#00aef7] border-r-2 border-[#00aef7]'
                          : disabled
                            ? 'text-gray-400 cursor-default'
                            : 'text-gray-600 hover:bg-gray-100 hover:text-gray-800'
                        }
                      `}
                      style={disabled ? { opacity: 0.5 } : undefined}
                    >
                      <Icon size={14} className="shrink-0" />
                      <span className="truncate">{sec.label}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </nav>

          {/* Right content panel */}
          <div className="flex-1 min-h-0 overflow-y-auto p-5 eh-help-nodrag">
            {renderPanel()}
          </div>
        </div>
        </div>{/* end h-full flex wrapper */}
      </Rnd>
    </div>
  );
}
